import { useState, useEffect, useRef } from 'react';
import { getCode, getNames } from 'country-list';
import '../App.tailwind.css';
import { useNavigate, useLocation } from 'react-router-dom';
// Import Supabase functions
import { saveAbandonedCheckout, getAbandonedCheckout, supabase, trackCheckoutEvent } from '../supabaseClient';
// Import Shopify cart service
import { createDraftOrderWithCustomPrice, getDraftOrderCheckoutUrl } from '../shopifyClient';
import { createWixPayment } from '../wixClient';
// Import geolocation utilities
import { detectUserLanguage, saveLanguagePreference, detectUserCountry, saveCountryPreference } from '../utils/geolocation';
// Import address service utilities
import { getCountryConfig, validatePostalCode } from '../utils/addressService';
// Import currency service utilities
import {
  detectUserCurrency,
  initializeCurrencyService,
  calculateDualCurrencyDisplay,
  getSupportedCurrencies,
  isUSDOnlyPaymentMethod,
  formatCurrency,
  convertFromUSD,
  convertToEUR,
  getExchangeRates,
  CURRENCY_SYMBOLS
} from '../utils/currencyService';
// Import custom components
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';
import CurrencyDisplay, { PriceDisplay, CurrencySelector, CurrencyInfo } from '../components/CurrencyDisplay';
// Import React icons
import {
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaLock,
  FaShieldAlt,
  FaCcStripe,
  FaCreditCard,
  FaShippingFast,
  FaHistory,
  FaGlobe
} from 'react-icons/fa';
import { SiKlarna } from 'react-icons/si';
import { BsCheckCircleFill } from 'react-icons/bs';
import { MdSecurity } from 'react-icons/md';
import Select from 'react-select';
import countryList from 'react-select-country-list';
// Add these imports
import StripeConnectOnboarding from '../components/StripeConnectOnboarding';

/** Create Stripe rotation checkout (or smart fallback URL from API). */
async function createStripeRotationCheckout(orderData) {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://pay.drivi.pro';
  const response = await fetch(`${apiUrl}/create-stripe-rotation-checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderData }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || `HTTP ${response.status}`);
  }
  if (!data.success || !data.checkoutUrl) {
    throw new Error(data.error || 'Failed to create rotation checkout');
  }
  if (data.mode === 'smart_fallback') {
    console.log(`↪️ Rotation smart fallback (${data.reason}):`, data.checkoutUrl);
  } else {
    console.log(`✅ Rotation checkout on account ${data.accountId}:`, data.checkoutUrl);
  }
  return data;
}

const EURO_COUNTRIES = [
  'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
  'AD', 'MC', 'SM', 'VA', 'ME', 'XK',
  'CH', 'DK', 'NO', 'SE', 'PL', 'CZ', 'HU', 'RO', 'BG', 'HR', 'IS', 'LI',
  'AL', 'BA', 'BY', 'GI', 'IM', 'JE', 'GG', 'MD', 'MK', 'RS', 'UA'
];

let lemonScriptPromise = null;

const loadLemonScript = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.LemonSqueezy) {
    return Promise.resolve();
  }

  if (lemonScriptPromise) {
    return lemonScriptPromise;
  }

  lemonScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://assets.lemonsqueezy.com/lemon.js';
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });

  return lemonScriptPromise;
};

// Currency flags mapping
const CURRENCY_FLAGS = {
  'USD': '🇺🇸',
  'EUR': '🇪🇺',
  'GBP': '🇬🇧',
  'JPY': '🇯🇵',
  'CAD': '🇨🇦',
  'AUD': '🇦🇺',
  'CHF': '🇨🇭',
  'CNY': '🇨🇳',
  'SEK': '🇸🇪',
  'NOK': '🇳🇴',
  'DKK': '🇩🇰',
  'PLN': '🇵🇱',
  'CZK': '🇨🇿',
  'HUF': '🇭🇺',
  'RUB': '🇷🇺',
  'BRL': '🇧🇷',
  'MXN': '🇲🇽',
  'INR': '🇮🇳',
  'KRW': '🇰🇷',
  'SGD': '🇸🇬',
  'HKD': '🇭🇰',
  'MYR': '🇲🇾',
  'IDR': '🇮🇩',
  'PHP': '🇵🇭',
  'VND': '🇻🇳',
  'TWD': '🇹🇼',
  'THB': '🇹🇭',
  'SAR': '🇸🇦',
  'AED': '🇦🇪',
  'EGP': '🇪🇬',
  'ZAR': '🇿🇦',
  'NGN': '🇳🇬',
  'GHS': '🇬🇭',
  'KES': '🇰🇪',
  'UGX': '🇺🇬',
  'TZS': '🇹🇿',
  'MAD': '🇲🇦',
  'TND': '🇹🇳',
  'DZD': '🇩🇿',
  'ILS': '🇮🇱',
  'TRY': '🇹🇷',
  'NZD': '🇳🇿'
};

// Stripe configuration
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.STRIPE_PUBLISHABLE_KEY;

// Constants for localStorage keys
const CART_STORAGE_KEY = 'checkout_cart_data';
const CUSTOMER_INFO_KEY = 'checkout_customer_info';
const STORE_NAME_KEY = 'checkout_store_name';
const STORE_DOMAIN_KEY = 'checkout_store_domain';
const NEW_CART_FLAG_KEY = 'new_cart_received'; // New flag to track new cart

const LANGUAGES = {
  en: {
    contactInfo: 'Contact information',
    emailPlaceholder: 'Email address *',
    newsOffers: 'Send me news and exclusive offers',
    shippingInfo: 'Shipping information',
    firstName: 'First name *',
    lastName: 'Last name *',
    address1: 'Street Address *',
    address2: 'Apt, suite, etc. (optional)',
    city: 'City *',
    postal: 'Postal code',
    state: 'State/Province *',
    phone: 'Phone number *',
    shippingMethod: 'Shipping Method',
    fedex: 'FedEx Express Shipping',
    deliveryEstimate: 'Delivery in 3–5 business days',
    recommended: 'Recommended',
    paymentMethod: 'Payment method:',
    secureCheckout: 'Secure Checkout',
    secureDesc: "All transactions are secure and encrypted. You'll be redirected to our payment processor to complete your purchase.",
    processPayment: 'Complete Secure Payment',
    reassurance: '256-bit SSL Encrypted · Free Returns · Money-back Guarantee',
    almostDone: 'Almost done! Complete your order below.',
    orderSummary: 'Order summary',
    price: 'Price',
    remove: 'Remove',
    discount: 'Discount',
    couponPlaceholder: 'Enter coupon code',
    apply: 'Apply',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    total: 'Total:',
    continueShopping: 'Continue Shopping',
    topBannerTagline: 'Secure checkout — Fast shipping on all orders',
    emptyCart: 'Your cart is empty',
    emptyCartDesc: 'Please add items to your cart before proceeding to checkout.',
    returnToShop: 'Return to Shop',
    // Currency translations
    currency: 'Currency',
    currencyNote: 'Prices shown in your local currency for convenience',
    usdOnlyNote: 'Checkout will be processed in USD',
    chargedInUSD: 'You\'ll be charged in USD',
    loadingRates: 'Loading exchange rates...',
    exchangeRateError: 'Unable to load exchange rates',
    cartTotalMessage: 'Cart total in {currency} will be charged {amount} USD.',
    usdChargeNote: 'You\'ll be charged in USD',
    checkoutProcessedUSD: 'Checkout will be processed in USD',
    // Smart payment translations
    specialDiscountOffer: 'Special Discount Offer!',
    freeShipping: 'Free Shipping',
    off: 'OFF',
    original: 'Original:',
    now: 'Now:',
    save: 'Save',
    offerExpiresIn: 'Offer expires in:',
    expiredToday: 'Expired today',
    proceedToCheckoutSave: 'Proceed to Checkout - ${amount} (Save ${saveAmount})',
    processedInUSD: 'All payments will be processed in USD',
    processedInEUR: 'All payments will be processed in EUR',
  },
  fr: {
    contactInfo: 'Informations de contact',
    emailPlaceholder: 'Adresse e-mail *',
    newsOffers: 'Recevoir des offres et actualités',
    shippingInfo: 'Informations de livraison',
    firstName: 'Prénom *',
    lastName: 'Nom *',
    address1: 'Adresse *',
    address2: 'Appartement, suite, etc. (optionnel)',
    city: 'Ville *',
    postal: 'Code postal',
    state: 'Région/Province *',
    phone: 'Numéro de téléphone *',
    shippingMethod: 'Mode de livraison',
    fedex: 'Livraison Express FedEx',
    deliveryEstimate: 'Livraison en 3–5 jours ouvrables',
    recommended: 'Recommandé',
    paymentMethod: 'Méthode de paiement :',
    secureCheckout: 'Paiement sécurisé',
    secureDesc: "Toutes les transactions sont sécurisées et cryptées. Vous serez redirigé vers notre processeur de paiement pour finaliser votre achat.",
    processPayment: 'Finaliser le paiement sécurisé',
    reassurance: 'Cryptage SSL 256-bit · Retours gratuits · Garantie satisfait ou remboursé',
    almostDone: 'Presque terminé ! Finalisez votre commande ci-dessous.',
    orderSummary: 'Résumé de la commande',
    price: 'Prix',
    remove: 'Supprimer',
    discount: 'Réduction',
    couponPlaceholder: 'Code promo',
    apply: 'Appliquer',
    subtotal: 'Sous-total',
    shipping: 'Livraison',
    total: 'Total :',
    continueShopping: 'Continuer vos achats',
    topBannerTagline: 'Paiement sécurisé — Livraison rapide sur toutes les commandes',
    emptyCart: 'Votre panier est vide',
    emptyCartDesc: 'Ajoutez des articles à votre panier avant de passer à la caisse.',
    returnToShop: 'Retour à la boutique',
    // Currency translations
    currency: 'Devise',
    currencyNote: 'Prix affichés dans votre devise locale pour votre commodité',
    usdOnlyNote: 'Le paiement sera traité en USD',
    chargedInUSD: 'Vous serez facturé en USD',
    loadingRates: 'Chargement des taux de change...',
    exchangeRateError: 'Impossible de charger les taux de change',
    cartTotalMessage: 'Total du panier en {currency} sera facturé {amount} USD.',
    usdChargeNote: 'Vous serez facturé en USD',
    checkoutProcessedUSD: 'Le paiement sera traité en USD',
    // Smart payment translations
    specialDiscountOffer: 'Offre de réduction spéciale !',
    freeShipping: 'Livraison gratuite',
    off: 'DE RÉDUCTION',
    original: 'Original :',
    now: 'Maintenant :',
    save: 'Économisez',
    offerExpiresIn: 'L\'offre expire dans :',
    expiredToday: 'Expire aujourd\'hui',
    proceedToCheckoutSave: 'Procéder au paiement - ${amount} (Économisez ${saveAmount})',
    processedInUSD: 'Tous les paiements seront traités en USD',
    processedInEUR: 'Tous les paiements seront traités en EUR',
  },
  es: {
    contactInfo: 'Información de contacto',
    emailPlaceholder: 'Correo electrónico *',
    newsOffers: 'Envíame noticias y ofertas exclusivas',
    shippingInfo: 'Información de envío',
    firstName: 'Nombre *',
    lastName: 'Apellido *',
    address1: 'Dirección *',
    address2: 'Apto, suite, etc. (opcional)',
    city: 'Ciudad *',
    postal: 'Código postal',
    state: 'Provincia/Estado *',
    phone: 'Número de teléfono *',
    shippingMethod: 'Método de envío',
    fedex: 'Envío Exprés FedEx',
    deliveryEstimate: 'Entrega en 3–5 días hábiles',
    recommended: 'Recomendado',
    paymentMethod: 'Método de pago:',
    secureCheckout: 'Pago seguro',
    secureDesc: 'Todas las transacciones son seguras y cifradas. Serás redirigido a nuestro procesador de pagos para completar tu compra.',
    processPayment: 'Completar pago seguro',
    reassurance: 'Cifrado SSL 256-bit · Devoluciones gratis · Garantía de devolución',
    almostDone: '¡Casi listo! Completa tu pedido a continuación.',
    orderSummary: 'Resumen del pedido',
    price: 'Precio',
    remove: 'Eliminar',
    discount: 'Descuento',
    couponPlaceholder: 'Introduce el código de cupón',
    apply: 'Aplicar',
    subtotal: 'Subtotal',
    shipping: 'Envío',
    total: 'Total:',
    continueShopping: 'Seguir comprando',
    topBannerTagline: 'Pago seguro — Envío rápido en todos los pedidos',
    emptyCart: 'Tu carrito está vacío',
    emptyCartDesc: 'Por favor, añade artículos a tu carrito antes de continuar con la compra.',
    returnToShop: 'Volver a la tienda',
    // Currency translations
    currency: 'Moneda',
    currencyNote: 'Precios mostrados en tu moneda local para tu comodidad',
    usdOnlyNote: 'El pago será procesado en USD',
    chargedInUSD: 'Se te cobrará en USD',
    loadingRates: 'Cargando tipos de cambio...',
    exchangeRateError: 'No se pudieron cargar los tipos de cambio',
    cartTotalMessage: 'Total del carrito en {currency} se cobrará {amount} USD.',
    usdChargeNote: 'Se te cobrará en USD',
    checkoutProcessedUSD: 'El pago será procesado en USD',
    // Smart payment translations
    specialDiscountOffer: '¡Oferta de descuento especial!',
    freeShipping: 'Envío gratis',
    off: 'DESCUENTO',
    original: 'Original:',
    now: 'Ahora:',
    save: 'Ahorra',
    offerExpiresIn: 'La oferta expira en:',
    expiredToday: 'Expira hoy',
    proceedToCheckoutSave: 'Proceder al pago - ${amount} (Ahorra ${saveAmount})',
    processedInUSD: 'Todos los pagos se procesarán en USD',
    processedInEUR: 'Todos los pagos se procesarán en EUR',
  },
  de: {
    contactInfo: 'Kontaktinformation',
    emailPlaceholder: 'E-Mail-Adresse *',
    newsOffers: 'Senden Sie mir Neuigkeiten und exklusive Angebote',
    shippingInfo: 'Versandinformation',
    firstName: 'Vorname *',
    lastName: 'Nachname *',
    address1: 'Straße und Hausnummer *',
    address2: 'Wohnung, Suite, etc. (optional)',
    city: 'Stadt *',
    postal: 'Postleitzahl',
    state: 'Bundesland *',
    phone: 'Telefonnummer *',
    shippingMethod: 'Versandart',
    fedex: 'FedEx Expressversand',
    deliveryEstimate: 'Lieferung in 3–5 Werktagen',
    recommended: 'Empfohlen',
    paymentMethod: 'Zahlungsmethode:',
    secureCheckout: 'Sichere Kasse',
    secureDesc: 'Alle Transaktionen sind sicher und verschlüsselt. Sie werden zu unserem Zahlungsabwickler weitergeleitet, um Ihren Einkauf abzuschließen.',
    processPayment: 'Sichere Zahlung abschließen',
    reassurance: '256-Bit SSL-Verschlüsselung · Kostenlose Rücksendungen · Geld-zurück-Garantie',
    almostDone: 'Fast fertig! Schließen Sie Ihre Bestellung ab.',
    orderSummary: 'Bestellübersicht',
    price: 'Preis',
    remove: 'Entfernen',
    discount: 'Rabatt',
    couponPlaceholder: 'Gutscheincode eingeben',
    apply: 'Anwenden',
    subtotal: 'Zwischensumme',
    shipping: 'Versand',
    total: 'Gesamt:',
    continueShopping: 'Weiter einkaufen',
    topBannerTagline: 'Sicher bezahlen — Schneller Versand für alle Bestellungen',
    emptyCart: 'Ihr Warenkorb ist leer',
    emptyCartDesc: 'Bitte fügen Sie Artikel zu Ihrem Warenkorb hinzu, bevor Sie zur Kasse gehen.',
    returnToShop: 'Zurück zum Shop',
    // Currency translations
    currency: 'Währung',
    currencyNote: 'Preise in Ihrer lokalen Währung zur Vereinfachung angezeigt',
    usdOnlyNote: 'Checkout wird in USD verarbeitet',
    chargedInUSD: 'Ihnen wird in USD berechnet',
    loadingRates: 'Wechselkurse werden geladen...',
    exchangeRateError: 'Wechselkurse konnten nicht geladen werden',
    cartTotalMessage: 'Warenkorb-Gesamtbetrag in {currency} wird {amount} USD berechnet.',
    usdChargeNote: 'Ihnen wird in USD berechnet',
    checkoutProcessedUSD: 'Checkout wird in USD verarbeitet',
    // Smart payment translations
    specialDiscountOffer: 'Sonderangebot!',
    freeShipping: 'Kostenloser Versand',
    off: 'RABATT',
    original: 'Original:',
    now: 'Jetzt:',
    save: 'Sparen',
    offerExpiresIn: 'Angebot läuft ab in:',
    expiredToday: 'Läuft heute ab',
    proceedToCheckoutSave: 'Zur Kasse - ${amount} (Sparen ${saveAmount})',
    processedInUSD: 'Alle Zahlungen werden in USD verarbeitet',
    processedInEUR: 'Alle Zahlungen werden in EUR verarbeitet',
  }
};

const getFlagEmoji = (countryCode) => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const countryOptions = countryList().getData().map(option => ({
  ...option,
  label: (
    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '20px', lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '18px' }}>
        {getFlagEmoji(option.value)}
      </span>
      {option.label}
    </span>
  )
}));

const CheckoutForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if shipping form should be hidden
  const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'wix';
  const lemonCheckoutUrl = process.env.REACT_APP_LEMON_CHECKOUT_URL || process.env.LEMON_CHECKOUT_URL || 'https://drivi.lemonsqueezy.com/checkout/buy/35a2bb3f-5b58-4398-b8a8-bc9d7cd41e3c?embed=1';
  const productType = process.env.REACT_APP_PRODUCT_TYPE || 'physical';
  const hideAnnouncement = process.env.REACT_APP_HIDE_ANNOUNCEMENT === 'true';
  const hidePromo = process.env.REACT_APP_HIDE_PROMO === 'true';
  const targetCurrency = process.env.REACT_APP_CURRENCY || 'USD'; // Target currency for smart-pro (USD or EUR)
  const displayShippingForm = process.env.REACT_APP_DISPLAY_SHIPPING_FORM === 'true';
  const hideAnnouncementFreeShipping = process.env.REACT_APP_HIDE_ANNOUNCEMENT_FREE_SHIPPING === 'true';
  const rotationMaxUsd = Number(
    process.env.REACT_APP_STRIPE_ROTATION_MAX_AMOUNT ||
      process.env.STRIPE_ROTATION_MAX_AMOUNT ||
      100
  );
  // Static hide (env / smart). Rotation >$100 also hides — applied after currencyData loads.
  const baseHideShippingForm =
    productType === 'digital' ||
    process.env.REACT_APP_HIDE_SHIPPING_FORM === 'true' ||
    (paymentMethod === 'smart' && !displayShippingForm);

  // Check if direct forward is enabled
  const directForward = process.env.REACT_APP_DIRECT_FORWARD === 'true';

  // Remove session reference approach - just check URL directly
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('US');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [postal, setPostal] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [coupon, setCoupon] = useState('');
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [store, setStore] = useState('');
  const [storeDomain, setStoreDomain] = useState('');
  const [formValid, setFormValid] = useState(false);
  // Set shipping fee to 0 if product type is digital or shipping is hidden
  const [shippingFee, setShippingFee] = useState(productType === 'digital' || baseHideShippingForm ? 0 : 700);
  const [shippingOption, setShippingOption] = useState('fedex');
  const [mobileOrderSummaryOpen, setMobileOrderSummaryOpen] = useState(false);

  // Save abandoned checkout state
  const [isSavingCheckout, setSavingCheckout] = useState(false);
  const [checkoutSaved, setCheckoutSaved] = useState(false);
  const [checkoutRecovered, setCheckoutRecovered] = useState(false);
  const [debugMessage, setDebugMessage] = useState('');

  // Shopify checkout state
  const [shopifyCheckoutUrl, setShopifyCheckoutUrl] = useState('');


  // Additional state variables for error handling and messages
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [orderNumber, setOrderNumber] = useState(() => {
    // Generate unique order number
    return 'ORD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  });
  const [currency, setCurrency] = useState('USD');

  // Currency conversion state
  const [userCurrency, setUserCurrency] = useState('USD');
  const [currencyData, setCurrencyData] = useState(null);
  const [exchangeRatesLoaded, setExchangeRatesLoaded] = useState(false);
  const [currencyError, setCurrencyError] = useState('');
  const [supportedCurrencies, setSupportedCurrencies] = useState(['USD']);

  // Direct forward processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  // Gumroad popup state
  const [showCheckoutPopup, setShowCheckoutPopup] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');

  // Reference to track if customer info has been loaded from Supabase
  const loadedFromSupabase = useRef(false);
  const initialLoadCompleted = useRef(false);
  const prevCountryRef = useRef(country);

  // Calculate total with shipping
  const totalWithShipping = total + shippingFee;

  // --- Original smart family (unchanged behavior for smart / smart-hide / smart-pro*) ---
  const isSmartFamily =
    paymentMethod === 'smart' ||
    paymentMethod === 'smart-hide' ||
    paymentMethod === 'smart-pro' ||
    paymentMethod === 'smart-pro-lemon';

  // --- Rotation method stays on rotation checkout even above $100 (smart fallback removed for rotation) ---
  const rotationUsdAmount =
    currencyData && typeof currencyData.usdAmount === 'number'
      ? Number(currencyData.usdAmount)
      : null;
  const rotationUsesSmart = false;

  // hideShippingForm: original rules + rotation over-threshold only
  const hideShippingForm = baseHideShippingForm || rotationUsesSmart;

  // Load / show smart-payment-links for classic smart family OR rotation fallback
  const usesSmartLinks = isSmartFamily || rotationUsesSmart;

  // Ensure shipping fee is 0 for digital products / hidden shipping
  useEffect(() => {
    if (productType === 'digital' || hideShippingForm) {
      setShippingFee(0);
    }
  }, [productType, hideShippingForm]);

  const [language, setLanguage] = useState('en');
  const [languageDetected, setLanguageDetected] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState('');
  const [countryDetected, setCountryDetected] = useState(false);
  const [countryConfig, setCountryConfig] = useState(getCountryConfig('US'));

  const [fieldValidation, setFieldValidation] = useState({
    phone: null,
    postal: null
  });

  // Smart payment state
  const [smartDiscount, setSmartDiscount] = useState(null);

  const t = LANGUAGES[language];

  // Automatic language detection on component mount
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        const detectedLang = await detectUserLanguage();
        setLanguage(detectedLang);
        setLanguageDetected(true);

        // Get detected country for display
        const savedCountry = localStorage.getItem('checkout_detected_country');
        if (savedCountry) {
          setDetectedCountry(savedCountry);
        }

        console.log('Language initialized:', detectedLang);
      } catch (error) {
        console.error('Error initializing language:', error);
        setLanguage('en');
        setLanguageDetected(true);
      }
    };

    initializeLanguage();
  }, []);

  // Initialize currency service
  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        console.log('🚀 Initializing currency service...');

        // Get payment method to determine supported currencies
        const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'wix';
        const supportedCurr = getSupportedCurrencies(paymentMethod);
        setSupportedCurrencies(supportedCurr);

        // Initialize currency service
        const result = await initializeCurrencyService();

        if (result.success) {
          setUserCurrency(result.userCurrency);
          setCurrency(result.userCurrency);
          setExchangeRatesLoaded(result.exchangeRatesLoaded);

          console.log('✅ Currency service initialized:', result.userCurrency);
        } else {
          console.error('❌ Currency service initialization failed:', result.error);
          setCurrencyError(result.error || 'Failed to initialize currency service');
        }
      } catch (error) {
        console.error('❌ Error initializing currency service:', error);
        setCurrencyError('Failed to initialize currency service');
      }
    };

    initializeCurrency();
  }, []);

  // Calculate currency display data when total or currency changes
  useEffect(() => {
    const calculateCurrencyDisplay = async () => {
      if (!exchangeRatesLoaded || !total) {
        return;
      }

      try {
        const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'wix';
        const finalTotal = hideShippingForm ? total : totalWithShipping;
        const totalInDollars = finalTotal / 100; // Convert cents to dollars including shipping

        const currencyDisplayData = await calculateDualCurrencyDisplay(
          totalInDollars,
          userCurrency,
          paymentMethod
        );

        setCurrencyData(currencyDisplayData);
        console.log('💱 Currency display data calculated:', currencyDisplayData);
        console.log('💱 Payment method:', paymentMethod);
        console.log('💱 User currency:', userCurrency);
        console.log('💱 Total in dollars (including shipping):', totalInDollars);
        console.log('💱 Hide shipping form:', hideShippingForm);
      } catch (error) {
        console.error('❌ Error calculating currency display:', error);
        setCurrencyError('Failed to calculate currency conversion');
      }
    };

    calculateCurrencyDisplay();
  }, [total, totalWithShipping, userCurrency, exchangeRatesLoaded, hideShippingForm]);

  // Fetch smart payment discount when payment method is smart, smart-hide, smart-pro, or smart-pro-lemon
  useEffect(() => {
    const fetchSmartDiscount = async () => {
      if (!usesSmartLinks || !total || !exchangeRatesLoaded) {
        setSmartDiscount(null);
        return;
      }

      try {
        const finalTotal = hideShippingForm ? total : totalWithShipping;

        // For smart-pro, use target currency (EUR or USD) from environment
        const useEUR = paymentMethod === 'smart-pro' && targetCurrency === 'EUR';

        // Convert total to target currency (USD or EUR)
        let totalInTargetCurrency;
        if (useEUR) {
          // Convert to EUR for smart-pro when CURRENCY=EUR
          if (currencyData && currencyData.usdAmount !== undefined) {
            // Convert USD to EUR
            const rates = await getExchangeRates();
            const eurRate = rates['EUR'] || 0.85;
            totalInTargetCurrency = currencyData.usdAmount * eurRate;
          } else {
            // Convert from user currency to EUR
            totalInTargetCurrency = await convertToEUR(finalTotal / 100, userCurrency);
          }
        } else {
          // Default: convert to USD
          if (currencyData && currencyData.usdAmount !== undefined) {
            totalInTargetCurrency = currencyData.usdAmount;
          } else {
            totalInTargetCurrency = finalTotal / 100; // Fallback: assume cents to USD
          }
        }

        // Load payment links JSON based on payment method
        const jsonFile = paymentMethod === 'smart-pro-lemon'
          ? '/smart-pro-lemon-payment-links.json'
          : '/smart-payment-links.json';

        const response = await fetch(jsonFile);
        const paymentLinksData = await response.json();

        // Find the closest lower price
        const validLinks = paymentLinksData.links
          .map(link => ({
            link: link.link,
            total: parseFloat(link.total)
          }))
          .filter(link => link.total <= totalInTargetCurrency)
          .sort((a, b) => b.total - a.total); // Sort descending to get closest lower value

        if (validLinks.length === 0) {
          setSmartDiscount(null);
          return;
        }

        const selectedLink = validLinks[0];
        const discountAmount = totalInTargetCurrency - selectedLink.total;
        const discountPercentage = ((discountAmount / totalInTargetCurrency) * 100).toFixed(2);

        // Convert target currency amounts to user's currency if different
        let originalTotalLocal = totalInTargetCurrency;
        let discountedTotalLocal = selectedLink.total;
        let discountAmountLocal = discountAmount;

        if (useEUR) {
          // If using EUR, convert EUR amounts to user's currency
          if (userCurrency !== 'EUR' && exchangeRatesLoaded) {
            const rates = await getExchangeRates();
            const eurRate = rates['EUR'] || 0.85;
            // Convert EUR -> USD -> user currency
            const usdOriginal = totalInTargetCurrency / eurRate;
            const usdDiscounted = selectedLink.total / eurRate;
            const usdDiscount = discountAmount / eurRate;
            originalTotalLocal = await convertFromUSD(usdOriginal, userCurrency);
            discountedTotalLocal = await convertFromUSD(usdDiscounted, userCurrency);
            discountAmountLocal = await convertFromUSD(usdDiscount, userCurrency);
          }
        } else {
          // Default: convert USD amounts to user's currency
          if (userCurrency !== 'USD' && exchangeRatesLoaded) {
            originalTotalLocal = await convertFromUSD(totalInTargetCurrency, userCurrency);
            discountedTotalLocal = await convertFromUSD(selectedLink.total, userCurrency);
            discountAmountLocal = await convertFromUSD(discountAmount, userCurrency);
          }
        }

        const discountData = {
          success: true,
          originalTotal: totalInTargetCurrency,
          discountedTotal: selectedLink.total,
          discountAmount: discountAmount,
          discountPercentage: parseFloat(discountPercentage),
          paymentLink: selectedLink.link,
          // Local currency amounts
          originalTotalLocal: originalTotalLocal,
          discountedTotalLocal: discountedTotalLocal,
          discountAmountLocal: discountAmountLocal,
          userCurrency: userCurrency,
          targetCurrency: useEUR ? 'EUR' : 'USD' // Store target currency
        };

        setSmartDiscount(discountData);
        console.log('✅ Smart payment discount found:', discountData);
      } catch (error) {
        console.error('❌ Error fetching smart payment discount:', error);
        setSmartDiscount(null);
      }
    };

    fetchSmartDiscount();
  }, [total, totalWithShipping, paymentMethod, hideShippingForm, exchangeRatesLoaded, userCurrency, currencyData, targetCurrency, usesSmartLinks]);

  // Automatic country detection on component mount
  useEffect(() => {
    const initializeCountry = async () => {
      try {
        const detectedCountryCode = await detectUserCountry();
        setCountry(detectedCountryCode);
        setCountryDetected(true);

        console.log('Country initialized:', detectedCountryCode);
      } catch (error) {
        console.error('Error initializing country:', error);
        setCountry('US');
        setCountryDetected(true);
      }
    };

    initializeCountry();
  }, []);

  // Update country configuration when country changes
  useEffect(() => {
    const config = getCountryConfig(country);
    setCountryConfig(config);
    console.log('Country config updated for:', country, config);

    // Auto-update currency ONLY if the country actually changed
    if (prevCountryRef.current !== country) {
      const code = (country || 'US').toUpperCase();
      let targetCurr = 'USD';
      if (code === 'GB') {
        targetCurr = 'GBP';
      } else if (EURO_COUNTRIES.includes(code)) {
        targetCurr = 'EUR';
      }

      if (userCurrency !== targetCurr) {
        handleCurrencyChange(targetCurr);
      }
      prevCountryRef.current = country;
    }
  }, [country, userCurrency]);

  // IMMEDIATELY check if we have a cart in URL, and if so, CLEAR ALL DATA
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('cart')) {
      console.log('NEW CART FROM URL - WIPING ALL DATA');

      // Save language and country preferences before clearing
      const savedLanguage = localStorage.getItem('checkout_language');
      const savedCountry = localStorage.getItem('checkout_country');
      const savedDetectedCountry = localStorage.getItem('checkout_detected_country');

      // CLEAR EVERYTHING
      localStorage.clear();

      // Restore language and country preferences
      if (savedLanguage) {
        localStorage.setItem('checkout_language', savedLanguage);
      }
      if (savedCountry) {
        localStorage.setItem('checkout_country', savedCountry);
      }
      if (savedDetectedCountry) {
        localStorage.setItem('checkout_detected_country', savedDetectedCountry);
      }

      // Set flag to prevent loading old cart
      localStorage.setItem(NEW_CART_FLAG_KEY, 'true');

      // Reset all form fields (but keep detected country)
      setEmail('');
      // Don't reset country here - let auto-detection handle it
      setFirstName('');
      setLastName('');
      setAddress1('');
      setAddress2('');
      setPostal('');
      setPhone('');
      setCity('');
      setState('');

      // Load the new cart ONLY
      loadCartFromUrl(searchParams);
    }
  }, []);

  // Initial setup - ONLY check URL first
  useEffect(() => {
    // Get country names and reorder to place 'United States' first
    const countryNames = getNames();
    const orderedCountries = ['United States', 'United Kingdom', ...countryNames
      .filter(name => name !== 'United States' && name !== 'United Kingdom')
      .sort()
    ];
    setCountries(orderedCountries);

    // CHECK URL FIRST
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('cart')) {
      // If cart in URL, clear everything and load fresh
      localStorage.clear();
      localStorage.setItem(NEW_CART_FLAG_KEY, 'true');
      loadCartFromUrl(searchParams);
    } else {
      // No cart in URL, try to load cart and store info from localStorage
      const savedCartData = localStorage.getItem(CART_STORAGE_KEY);
      const savedStoreName = localStorage.getItem(STORE_NAME_KEY);
      const savedStoreDomain = localStorage.getItem(STORE_DOMAIN_KEY);
      const savedCustomerInfo = localStorage.getItem(CUSTOMER_INFO_KEY);

      console.log('Loading saved data from localStorage...');

      // Load store info first
      if (savedStoreName) {
        setStore(savedStoreName);
      }
      if (savedStoreDomain) {
        setStoreDomain(savedStoreDomain);
      } else {
        setStoreDomain('drivi.store');
        localStorage.setItem(STORE_DOMAIN_KEY, 'drivi.store');
      }

      // Load customer info if available
      if (savedCustomerInfo) {
        try {
          const customerInfo = JSON.parse(savedCustomerInfo);
          console.log('Found saved customer info:', customerInfo);

          // Load all form fields except country (let auto-detection handle country)
          setEmail(customerInfo.email || '');
          setFirstName(customerInfo.firstName || '');
          setLastName(customerInfo.lastName || '');
          setAddress1(customerInfo.address1 || '');
          setAddress2(customerInfo.address2 || '');
          setPostal(customerInfo.postal || '');
          setPhone(customerInfo.phone || '');
          setCity(customerInfo.city || '');
          setState(customerInfo.state || '');
          // Only set country from saved data if no auto-detection is happening
          if (customerInfo.country && !localStorage.getItem('checkout_country')) {
            setCountry(customerInfo.country);
          }

          console.log('Restored all form fields from saved data');
        } catch (error) {
          console.error('Error parsing saved customer info:', error);
        }
      } else {
        // Try to load email separately if no customer info
        const savedEmail = localStorage.getItem('checkout_email');
        if (savedEmail) {
          setEmail(savedEmail);
          console.log('Loaded email from separate storage:', savedEmail);
        }
      }

      // Then load cart if available
      if (savedCartData) {
        try {
          const cartData = JSON.parse(savedCartData);
          if (cartData.items && cartData.items.length > 0) {
            setCartItems(cartData.items);
            setTotal(cartData.total || 0);
            setShippingFee(cartData.shippingFee || 700);
            console.log('Loaded cart from localStorage:', cartData.items.length, 'items');
          }
        } catch (error) {
          console.error('Error parsing cart data:', error);
        }
      }
    }
  }, []);

  // Function to load cart from URL - NO LOCALSTORAGE
  const loadCartFromUrl = (searchParams) => {
    try {
      const cartData = searchParams.get('cart');
      if (!cartData) {
        console.error('No cart data in URL');
        return false;
      }

      const decryptedCart = JSON.parse(decodeURIComponent(cartData));

      // Set cart state AND save to localStorage for persistence
      setCartItems(decryptedCart.items);
      setTotal(decryptedCart.total);

      // Save cart data to localStorage for persistence
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        items: decryptedCart.items,
        total: decryptedCart.total,
        shippingFee: 700
      }));

      // Set and save store info
      const storeName = searchParams.get('store') || 'Drivi';
      const domain = searchParams.get('shop_domain') || 'drivi.store';

      // Update state
      setStore(storeName);
      setStoreDomain(domain);

      // Save to localStorage
      localStorage.setItem(STORE_NAME_KEY, storeName);
      localStorage.setItem(STORE_DOMAIN_KEY, domain);

      console.log('Saved store info:', { storeName, domain });

      // Clean the URL
      window.history.replaceState(null, '', window.location.pathname);

      console.log(`NEW CART LOADED AND SAVED: ${decryptedCart.items.length} items`);
      setDebugMessage("Fresh cart loaded and saved");
      setTimeout(() => setDebugMessage(''), 3000);

      return true;
    } catch (error) {
      console.error('Error processing cart from URL:', error);
      setDebugMessage("Error loading cart from URL");
      return false;
    }
  };

  // Check for URL changes - CLEAR ALL DATA if new cart
  useEffect(() => {
    if (!initialLoadCompleted.current) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('cart')) {
      console.log('URL changed - new cart detected - clearing all data');

      // CLEAR EVERYTHING
      localStorage.clear();

      // Reset all form fields
      setEmail('');
      setCountry('US');
      setFirstName('');
      setLastName('');
      setAddress1('');
      setAddress2('');
      setPostal('');
      setPhone('');
      setCity('');
      setState('');

      // Load new cart
      loadCartFromUrl(searchParams);

      setDebugMessage("New cart received - all previous data cleared!");
      setTimeout(() => setDebugMessage(''), 3000);
    }
  }, [location.search]);

  // Auto-redirect effect for direct forward when cart is loaded
  useEffect(() => {
    const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'shopify';

    // Only auto-redirect if:
    // 1. Direct forward is enabled
    // 2. Payment method is Wix
    // 3. We have cart items
    // 4. We're not already processing
    // 5. We're not already loading
    if (directForward &&
      paymentMethod === 'wix' &&
      cartItems.length > 0 &&
      !isProcessing &&
      !loading) {

      console.log('🚀 Auto-redirecting with direct forward enabled');

      // Set default customer info if not provided
      const defaultEmail = email || 'customer@example.com';
      const defaultFirstName = firstName || 'Customer';
      const defaultLastName = lastName || 'Customer';

      // Trigger automatic payment creation
      const autoCreatePayment = async () => {
        try {
          setIsProcessing(true);
          setProcessingMessage('Auto-creating secure payment link...');

          // Calculate total
          const finalTotalInCents = hideShippingForm ? total : total + shippingFee;

          // Prepare order data
          const orderData = {
            cartItems: [{
              title: cartItems[0]?.title || 'Product',
              quantity: cartItems[0]?.quantity || 1,
              price: (cartItems[0]?.price || finalTotalInCents) / 100
            }],
            totalAmount: finalTotalInCents / 100,
            localTotalAmount: currencyData ? currencyData.paymentAmount : (finalTotalInCents / 100),
            currency: currencyData ? currencyData.paymentCurrency : 'USD',
            country: country || 'US',
            email: defaultEmail,
            firstName: defaultFirstName,
            lastName: defaultLastName,
            phone: phone || '',
            includeShipping: !hideShippingForm,
            shippingAddress: {
              street: address1 || '',
              city: city || '',
              state: state || '',
              zipCode: postal || '',
              country: country || 'US'
            }
          };

          console.log(`🔍 Auto-creating ${paymentMethod} payment with data:`, JSON.stringify(orderData, null, 2));

          setProcessingMessage('Processing payment details...');

          let paymentResult;

          // Handle different payment methods
          if (paymentMethod === 'wix') {
            const { createWixPayment } = await import('../wixClient');
            paymentResult = await createWixPayment(orderData);
          } else if (paymentMethod === 'stripe') {
            // Create Stripe checkout session
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
            const response = await fetch(`${apiUrl}/create-stripe-checkout`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orderData }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
          } else if (paymentMethod === 'rotation') {
            if (rotationUsesSmart) {
              if (!smartDiscount || !smartDiscount.paymentLink) {
                throw new Error('Discount expired or no payment link available. Please refresh the page.');
              }
              paymentResult = { success: true, checkoutUrl: smartDiscount.paymentLink };
            } else {
              const data = await createStripeRotationCheckout(orderData);
              paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
            }
          } else if (paymentMethod === 'lemonsqueezy') {
            // Create LemonSqueezy checkout session
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
            const response = await fetch(`${apiUrl}/create-lemonsqueezy-checkout`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orderData }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
          } else if (paymentMethod === 'shopify') {
            // Create Shopify draft order
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
            const response = await fetch(`${apiUrl}/create-draft-order`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orderData }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
          } else if (paymentMethod === 'mypos') {
            // Create myPOS checkout session
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
            const response = await fetch(`${apiUrl}/create-mypos-checkout`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orderData }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
          } else if (paymentMethod === 'gumroad') {
            // Handle Gumroad (popup or redirect)
            const gumroadBaseUrl = process.env.REACT_APP_GUMROAD_BASE_URL || 'https://drivishop.gumroad.com/l/mylamb';
            const enableCheckoutPopup = process.env.REACT_APP_GUMROAD_ENABLE_CHECKOUT_POPUP === 'true';

            if (enableCheckoutPopup) {
              // Use modal for Gumroad
              const modalUrl = `${gumroadBaseUrl}?wanted=true&price=${(finalTotalInCents / 100).toFixed(2)}&email=${encodeURIComponent(email)}`;
              setModalUrl(modalUrl);
              setModalLoading(true);
              setShowModal(true);
              return; // Don't redirect, just show modal
            } else {
              // Use redirect for Gumroad
              const checkoutUrl = `${gumroadBaseUrl}?wanted=true&price=${(finalTotalInCents / 100).toFixed(2)}&email=${encodeURIComponent(email)}`;
              paymentResult = { success: true, checkoutUrl };
            }
          }

          if (paymentResult && paymentResult.success && paymentResult.checkoutUrl) {
            setProcessingMessage('Redirecting to secure checkout...');

            // Small delay to show the processing message
            await new Promise(resolve => setTimeout(resolve, 1000));

            console.log(`✅ Auto-redirect: ${paymentMethod} checkout URL received:`, paymentResult.checkoutUrl);

            // Redirect to checkout
            window.location.href = paymentResult.checkoutUrl;
          } else {
            throw new Error(paymentResult?.error || `Failed to create ${paymentMethod} payment link`);
          }

        } catch (error) {
          console.error('❌ Auto-redirect failed:', error);
          setError(`Auto-redirect failed: ${error.message}`);
          setIsProcessing(false);
          setProcessingMessage('');
        }
      };

      // Add a small delay to ensure everything is loaded
      const timer = setTimeout(autoCreatePayment, 1000);

      return () => clearTimeout(timer);
    }
  }, [cartItems, directForward, email, firstName, lastName, phone, address1, city, state, postal, country, total, shippingFee, hideShippingForm, isProcessing, loading]);

  // Function to load data from localStorage
  const loadFromLocalStorage = () => {
    // If we received a new cart, don't load anything from localStorage
    if (localStorage.getItem(NEW_CART_FLAG_KEY) === 'true') {
      console.log('New cart flag is set - skipping localStorage load');
      return;
    }

    try {
      const savedStoreDomain = localStorage.getItem(STORE_DOMAIN_KEY);
      if (savedStoreDomain) {
        setStoreDomain(savedStoreDomain);
      } else {
        setStoreDomain('drivi.cc');
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  };

  // REMOVE the email effect that loads abandoned checkout
  useEffect(() => {
    // Do nothing if we have a new cart
    if (localStorage.getItem(NEW_CART_FLAG_KEY) === 'true') {
      return;
    }

    // Only save email, never load old data
    if (email && email.includes('@')) {
      localStorage.setItem('checkout_email', email);
    }
  }, [email]);

  // Debug localStorage on mount
  useEffect(() => {
    try {
      // Check if localStorage is available
      if (typeof window !== 'undefined' && window.localStorage) {
        // Test localStorage
        localStorage.setItem('test_storage', 'test');
        const testValue = localStorage.getItem('test_storage');
        localStorage.removeItem('test_storage');

        if (testValue === 'test') {
          console.log('localStorage is working correctly');
          setDebugMessage('');
        } else {
          console.error('localStorage test failed - values don\'t match');
          setDebugMessage('WARNING: Browser storage may be disabled.');
        }
      } else {
        console.error('localStorage is not available in this browser');
        setDebugMessage('ERROR: Browser storage is not available');
      }
    } catch (e) {
      console.error('localStorage test exception:', e);
      setDebugMessage('ERROR: ' + e.message);
    }

    // Check for existing data
    try {
      const savedCustomerInfo = localStorage.getItem(CUSTOMER_INFO_KEY);
      const savedCartData = localStorage.getItem(CART_STORAGE_KEY);
      console.log('Existing localStorage data:', {
        savedCustomerInfo: savedCustomerInfo ? 'exists' : 'missing',
        savedCartData: savedCartData ? 'exists' : 'missing',
        email: localStorage.getItem('checkout_email') || 'missing'
      });
    } catch (e) {
      console.error('Error checking existing data:', e);
    }
  }, []);

  // Check if there are form changes to trigger abandoned checkout save
  useEffect(() => {
    // Only save if we have an email and cart items
    if (email && cartItems.length > 0 && !checkoutSaved && !isSavingCheckout) {
      // Use a debounce timer to avoid too many saves
      const timer = setTimeout(() => {
        console.log('Saving checkout data to Supabase for:', email);
        saveCheckoutToSupabase();
      }, 3000); // Save after 3 seconds of inactivity

      return () => clearTimeout(timer);
    }
  }, [email, firstName, lastName, address1, city, state, phone, cartItems, total]);

  // Reset checkoutSaved when email changes
  useEffect(() => {
    // Reset the saved state when email changes
    setCheckoutSaved(false);
  }, [email]);

  // Function to save checkout to Supabase
  const saveCheckoutToSupabase = async () => {
    // Only save if we have an email (the main identifier)
    if (!email || isSavingCheckout) return;

    setSavingCheckout(true);

    const checkoutData = {
      email,
      cart: {
        items: cartItems,
        total,
        shippingFee
      },
      shipping: {
        firstName,
        lastName,
        address1,
        address2,
        postal,
        city,
        state,
        country,
        phone
      },
      total: totalWithShipping
    };

    try {
      const result = await saveAbandonedCheckout(checkoutData, 'abandoned');
      if (result.success) {
        setCheckoutSaved(true);
        console.log('Abandoned checkout saved successfully');

        // Track the event
        await trackCheckoutEvent(email, 'cart_updated', {
          item_count: cartItems.length,
          total: totalWithShipping
        });
      } else {
        console.error('Failed to save abandoned checkout:', result.error);
      }
    } catch (error) {
      console.error('Error saving abandoned checkout:', error);
    } finally {
      setSavingCheckout(false);
    }
  };

  // Save cart data ONLY if we haven't received a new cart
  useEffect(() => {
    if (cartItems.length > 0 && total > 0 && localStorage.getItem(NEW_CART_FLAG_KEY) !== 'true') {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        items: cartItems,
        total,
        shippingFee
      }));
    }
  }, [cartItems, total, shippingFee]);

  // Save form data to localStorage whenever it changes (if not a new cart)
  useEffect(() => {
    if (localStorage.getItem(NEW_CART_FLAG_KEY) !== 'true') {
      try {
        const customerInfo = {
          email,
          country,
          firstName,
          lastName,
          address1,
          address2,
          postal,
          phone,
          city,
          state
        };

        // Save complete customer info
        localStorage.setItem(CUSTOMER_INFO_KEY, JSON.stringify(customerInfo));

        // Also save email separately for compatibility
        if (email) {
          localStorage.setItem('checkout_email', email);
        }

        console.log('Saved form data to localStorage');
      } catch (error) {
        console.error('Error saving form data:', error);
      }
    }
  }, [email, country, firstName, lastName, address1, address2, postal, phone, city, state]);

  // Save store name when it changes
  useEffect(() => {
    if (store) {
      localStorage.setItem(STORE_NAME_KEY, store);
    }
  }, [store]);

  // Validate form fields whenever they change
  useEffect(() => {
    // Check if all required fields are filled based on country configuration
    const isStateRequired = !!countryConfig.stateRequired;
    const isPostalRequired = !!countryConfig.postalRequired;

    const isFormValid = hideShippingForm ? true : (
      email.trim() !== '' &&
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      address1.trim() !== '' &&
      city.trim() !== '' &&
      (!isStateRequired || state.trim() !== '') &&
      (!isPostalRequired || postal.trim() !== '') &&
      phone.trim() !== ''
    );

    setFormValid(isFormValid);
  }, [email, firstName, lastName, address1, city, state, postal, phone, hideShippingForm, countryConfig]);

  const TrustBadges = () => {
    return (
      <div className="trust-badges-container">
        <div className="trust-badge">
          <div className="trust-badge-item">
            <MdSecurity size={24} />
            <span>Norton Secured</span>
          </div>
          <div className="trust-badge-spacer"></div>
          <div className="trust-badge-item">
            <FaShieldAlt size={24} />
            <span>SSL Secured</span>
          </div>
          <div className="trust-badge-spacer"></div>
          <div className="trust-badge-item">
            <BsCheckCircleFill size={24} />
            <span>Verisign Secured</span>
          </div>
        </div>
      </div>
    );
  };

  const PaymentIcons = () => {
    return (
      <div className="payment-icons">
        <FaCcVisa className="payment-icon" size={40} />
        <FaCcMastercard className="payment-icon" size={40} />
        <FaCcAmex className="payment-icon" size={40} />
        <FaCreditCard className="payment-icon" size={40} />
        <SiKlarna className="payment-icon" size={40} />
      </div>
    );
  };



  /**
   * Smart-hide: register destination on API, then open short bridge URL (no ?to=gumroad… in address bar).
   */
  const openSmartHideBridge = async (destinationUrl) => {
    const apiBase = (process.env.REACT_APP_API_URL || 'https://pay.drivi.pro').replace(/\/$/, '');
    const res = await fetch(`${apiBase}/api/smart-hide/bridge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        destination: destinationUrl,
        returnUrl: `${window.location.origin}/checkout`,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.bridgeUrl) {
      throw new Error(data.error || `Bridge failed (${res.status})`);
    }
    window.location.href = data.bridgeUrl;
  };

  const redirectToPaymentOrBridge = async (rawCheckoutUrl) => {
    if (paymentMethod === 'smart-hide' || paymentMethod === 'paypal' || paymentMethod === 'lemonsqueezy') {
      try {
        await openSmartHideBridge(rawCheckoutUrl);
      } catch (err) {
        console.warn('Smart-hide bridge failed, using legacy /r hop:', err);
        const legacy = `https://pay.drivi.pro/r?direct=1&to=${encodeURIComponent(rawCheckoutUrl)}&back=${encodeURIComponent(`${window.location.origin}/checkout`)}`;
        window.location.href = legacy;
      }
      return;
    }
    window.location.href = rawCheckoutUrl;
  };

  /**
   * Handle form submission - Process payment based on configured payment method
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      console.log('Processing checkout...');

      // Validate required form fields
      // For smart payment methods and smart-pro-lemon with hidden shipping, email is not required
      // Rotation over-threshold uses the same rules as classic `smart` (no contact form)
      if (
        paymentMethod !== 'smart' &&
        !(paymentMethod === 'smart-hide' && hideShippingForm) &&
        !(paymentMethod === 'smart-pro-lemon' && hideShippingForm) &&
        !rotationUsesSmart
      ) {
        if (!email || !firstName || !lastName) {
          throw new Error('Please fill in all required fields (email, first name, last name)');
        }
      } else {
        // For smart payment methods / rotation smart fallback, only validate discount + payment link
        if (!smartDiscount || !smartDiscount.paymentLink) {
          throw new Error('Discount expired or no payment link available. Please refresh the page.');
        }
      }

      // Calculate final amounts
      const finalTotalInCents = totalWithShipping;
      const finalTotalInDollars = (finalTotalInCents / 100).toFixed(2);

      console.log(`Processing order total: $${finalTotalInDollars}`);

      // Use payment method from component scope (already declared at component level)
      console.log(`Using payment method: ${paymentMethod}`);

      let checkoutUrl;

      if (paymentMethod === 'stripe') {
        // Process Stripe payment
        const orderData = {
          cartItems: cartItems.map(item => ({
            title: item.title || 'Product',
            name: item.title || 'Product',
            description: item.description || 'Product from checkout',
            quantity: item.quantity || 1,
            price: (item.price || 0) / 100, // Convert cents to dollars
            image: item.image || null
          })),
          totalAmount: finalTotalInCents / 100, // Convert cents to dollars
          localTotalAmount: currencyData ? currencyData.paymentAmount : (finalTotalInCents / 100),
          currency: currencyData ? currencyData.paymentCurrency : 'USD',
          country: country || 'US',
          shippingFee: hideShippingForm ? 0 : (shippingFee / 100), // Convert cents to dollars
          email: email,
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          orderNumber: orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US'
          } : null
        };

        console.log('🔍 Creating Stripe checkout session:', JSON.stringify(orderData, null, 2));

        try {
          const apiUrl = (process.env.REACT_APP_API_URL || 'https://pay.drivi.store').replace(/\/$/, '');
          const response = await fetch(`${apiUrl}/create-stripe-checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orderData }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data.success && data.checkoutUrl) {
            checkoutUrl = data.checkoutUrl;
            console.log('✅ Stripe checkout URL created:', checkoutUrl);
          } else {
            throw new Error('Failed to create Stripe checkout session');
          }
        } catch (error) {
          console.error('❌ Error creating Stripe checkout:', error);
          throw new Error('Failed to create Stripe checkout session');
        }
      } else if (paymentMethod === 'rotation' && !rotationUsesSmart) {
        const orderData = {
          cartItems: cartItems.map(item => ({
            title: item.title || 'Product',
            name: item.title || 'Product',
            description: item.description || 'Product from checkout',
            quantity: item.quantity || 1,
            price: (item.price || 0) / 100,
            image: item.image || null
          })),
          totalAmount: finalTotalInCents / 100,
          localTotalAmount: currencyData ? currencyData.paymentAmount : (finalTotalInCents / 100),
          currency: currencyData ? currencyData.paymentCurrency : 'USD',
          country: country || 'US',
          shippingFee: hideShippingForm ? 0 : (shippingFee / 100),
          email: email,
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          orderNumber: orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US'
          } : null
        };

        console.log('🔍 Creating Stripe ROTATION checkout:', JSON.stringify(orderData, null, 2));
        const data = await createStripeRotationCheckout(orderData);
        checkoutUrl = data.checkoutUrl;
      } else if (paymentMethod === 'lemonsqueezy') {
        // Process LemonSqueezy payment
        const orderData = {
          cartItems: cartItems.map(item => ({
            title: item.title || 'Product',
            name: item.title || 'Product',
            description: item.description || 'Product from checkout',
            quantity: item.quantity || 1,
            price: (item.price || 0) / 100, // Convert cents to dollars
            image: item.image || null
          })),
          totalAmount: finalTotalInCents / 100, // Convert cents to dollars
          shippingFee: hideShippingForm ? 0 : (shippingFee / 100), // Convert cents to dollars
          email: email,
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          orderNumber: orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US'
          } : null
        };

        console.log('🔍 Creating LemonSqueezy checkout session:', JSON.stringify(orderData, null, 2));

        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'https://pay.drivi.pro';
          const response = await fetch(`${apiUrl}/create-lemonsqueezy-checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orderData }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data.success && data.checkoutUrl) {
            checkoutUrl = data.checkoutUrl;
            console.log('✅ LemonSqueezy checkout URL created:', checkoutUrl);
          } else {
            throw new Error(data.error || 'Failed to create LemonSqueezy checkout session');
          }
        } catch (error) {
          console.error('❌ Error creating LemonSqueezy checkout:', error);
          throw new Error('Failed to create LemonSqueezy checkout session');
        }
      } else if (paymentMethod === 'mypos') {
        // Process myPOS payment
        const orderData = {
          cartItems: cartItems.map(item => ({
            title: item.title || 'Product',
            name: item.title || 'Product',
            description: item.description || 'Product from checkout',
            quantity: item.quantity || 1,
            price: (item.price || 0) / 100, // Convert cents to dollars
            image: item.image || null
          })),
          totalAmount: finalTotalInCents,
          currency: currency,
          orderNumber: orderNumber,
          email: email,
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          billingAddress: {
            address1: address1,
            city: city,
            province: state,
            zip: postal,
            country: country
          },
          currencyData: currencyData,
          originalAmountUSD: finalTotalInDollars
        };

        console.log('🔍 Creating myPOS checkout session:', JSON.stringify(orderData, null, 2));

        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
          const response = await fetch(`${apiUrl}/create-mypos-checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orderData }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data.success && data.checkoutUrl) {
            checkoutUrl = data.checkoutUrl;
            console.log('✅ myPOS checkout URL created:', checkoutUrl);
          } else {
            throw new Error(data.error || 'Failed to create myPOS checkout session');
          }
        } catch (error) {
          console.error('❌ Error creating myPOS checkout:', error);
          throw new Error('Failed to create myPOS checkout session');
        }
      } else if (paymentMethod === 'gumroad') {
        // Process Gumroad payment
        const gumroadBaseUrl = process.env.REACT_APP_GUMROAD_BASE_URL || 'https://drivishop.gumroad.com/l/mylamb';
        const enableCheckoutPopup = process.env.REACT_APP_GUMROAD_ENABLE_CHECKOUT_POPUP === 'true';

        console.log('🔍 Gumroad payment processing:');
        console.log('🔍 Enable popup:', enableCheckoutPopup);
        console.log('🔍 Gumroad URL:', gumroadBaseUrl);

        if (enableCheckoutPopup) {
          // Use checkout modal instead of redirect
          const modalUrl = `${gumroadBaseUrl}?wanted=true&price=${finalTotalInDollars}&email=${encodeURIComponent(email)}`;
          console.log('✅ Gumroad checkout modal URL created:', modalUrl);

          // Open modal popup with loading state
          setModalUrl(modalUrl);
          setModalLoading(true);
          setShowModal(true);

          // Don't redirect, just return the modal URL for reference
          checkoutUrl = modalUrl;
          console.log('✅ Gumroad modal opened successfully');

          // IMPORTANT: Return early to prevent redirect
          return;
        } else {
          // Use redirect (original behavior)
          checkoutUrl = `${gumroadBaseUrl}?wanted=true&price=${finalTotalInDollars}&email=${encodeURIComponent(email)}`;
          setShowModal(false);
          console.log('✅ Gumroad checkout URL created (redirect):', checkoutUrl);
        }
      } else if (paymentMethod === 'paypal') {
        // Process PayPal invoice and get payer-view link
        const apiUrl = process.env.REACT_APP_API_URL || 'https://pay.drivi.pro';
        const paypalOrderData = {
          cartItems,
          totalAmount: Number(finalTotalInDollars), // dollars
          email,
          firstName,
          lastName,
          phone,
          orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US',
          } : null,
        };
        const response = await fetch(`${apiUrl}/create-paypal-invoice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderData: paypalOrderData }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || `PayPal invoice failed (${response.status})`);
        }
        const data = await response.json();
        if (!data.success || !data.checkoutUrl) {
          throw new Error(data.error || 'Failed to create PayPal invoice');
        }
        checkoutUrl = data.checkoutUrl;
        console.log('✅ PayPal invoice checkout URL:', checkoutUrl);
      } else if (isSmartFamily || rotationUsesSmart) {
        // Process Smart payment (also used by rotation when cart USD > max)
        if (!smartDiscount || !smartDiscount.paymentLink) {
          throw new Error('Discount expired or no payment link available. Please refresh the page.');
        }

        // Use the payment link from smart discount
        checkoutUrl = smartDiscount.paymentLink;
        console.log(
          rotationUsesSmart
            ? '↪️ Rotation >$100 → smart payment link:'
            : '✅ Smart payment checkout URL:',
          checkoutUrl
        );
        console.log('💰 Discount applied:', {
          originalTotal: smartDiscount.originalTotal,
          discountedTotal: smartDiscount.discountedTotal,
          discountPercentage: smartDiscount.discountPercentage
        });

        // Save checkout data to Supabase for order tracking (Smart payment)
        if (supabase) {
          try {
            // Use placeholder email for smart payment since email is required but not collected
            const placeholderEmail = `order-${orderNumber}@smart-checkout.local`;
            // Use actual email for smart-pro, smart-hide, and smart-pro-lemon when shipping is not hidden
            const trackedEmail = (paymentMethod === 'smart-pro' || (paymentMethod === 'smart-hide' && !hideShippingForm) || (paymentMethod === 'smart-pro-lemon' && !hideShippingForm)) && email ? email : placeholderEmail;

            // Use only the columns that exist in the schema: email, cart_data, shipping_info, total, status
            const checkoutData = {
              email: trackedEmail, // Required by schema
              cart_data: {
                items: cartItems, // Cart items as JSONB
                order_number: orderNumber,
                original_total: smartDiscount.originalTotal,
                discounted_total: smartDiscount.discountedTotal,
                discount_percentage: smartDiscount.discountPercentage,
                currency: smartDiscount.targetCurrency || smartDiscount.userCurrency || 'USD',
                payment_method: paymentMethod,
                checkout_url: checkoutUrl
              },
              shipping_info: (paymentMethod === 'smart-pro' || (paymentMethod === 'smart-hide' && !hideShippingForm) || (paymentMethod === 'smart-pro-lemon' && !hideShippingForm)) ? {
                firstName: firstName || null,
                lastName: lastName || null,
                address1: address1 || null,
                address2: address2 || null,
                city: city || null,
                state: state || null,
                postal: postal || null,
                country: country || null,
                phone: phone || null
              } : {}, // Empty JSON object for smart
              total: smartDiscount.originalTotal, // Total amount (required by schema)
              status: 'pending' // Status
            };

            const { error: supabaseError } = await supabase
              .from('abandoned_checkouts')
              .insert([checkoutData]);

            if (supabaseError) {
              console.error('Smart payment order tracking save failed (non-blocking):', supabaseError);
              console.error('Error details:', JSON.stringify(supabaseError, null, 2));
            } else {
              console.log('✅ Smart payment order saved to Supabase:', orderNumber);
              console.log('📦 Cart items saved:', cartItems.length, 'items');
            }
          } catch (error) {
            console.error('Error saving smart payment order to Supabase (non-blocking):', error);
            console.error('Error stack:', error.stack);
          }
        } else {
          console.warn('Supabase not configured - skipping smart payment order tracking');
        }

        // Save pending order to Neon Database via Backend API (non-blocking)
        try {
          const backendApi = (process.env.REACT_APP_API_URL || 'https://pay.drivi.pro').replace(/\/$/, '');
          const orderDataForBackend = {
            orderNumber: orderNumber,
            email: email || `order-${orderNumber}@smart-checkout.local`,
            firstName: firstName || '',
            lastName: lastName || '',
            phone: phone || '',
            currency: smartDiscount.targetCurrency || smartDiscount.userCurrency || 'USD',
            totalAmount: smartDiscount.originalTotal,
            country: country || 'US',
            cartItems: cartItems
          };
          await fetch(`${backendApi}/create-smart-pending-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderData: orderDataForBackend })
          }).catch(e => console.error('Failed to notify backend of smart order:', e));
        } catch (backendErr) {
          console.error('Error initiating backend save:', backendErr);
        }
      } else if (paymentMethod === 'wix') {
        // Check if direct forward is enabled for Wix payments
        if (directForward) {
          // Show processing state and create payment link in background
          setIsProcessing(true);
          setProcessingMessage('Creating secure payment link...');

          // Prepare order data with all form information
          const orderData = {
            cartItems: [{
              title: cartItems[0]?.title || 'Product',
              quantity: cartItems[0]?.quantity || 1,
              price: cartItems[0]?.price || finalTotalInCents
            }],
            totalAmount: finalTotalInCents,
            email: email,
            firstName: firstName,
            lastName: lastName,
            phone: phone,
            includeShipping: !hideShippingForm,
            shippingAddress: {
              street: address1 || '',
              city: city || '',
              state: state || '',
              zipCode: postal || '',
              country: country || 'US'
            }
          };

          console.log('🔍 Creating Wix payment with direct forward:', JSON.stringify(orderData, null, 2));

          setProcessingMessage('Processing payment details...');

          // Create Wix payment in background
          const wixPayment = await createWixPayment(orderData);
          checkoutUrl = wixPayment.checkoutUrl;

          setProcessingMessage('Redirecting to secure checkout...');

          // Small delay to show the processing message
          await new Promise(resolve => setTimeout(resolve, 1000));

          setIsProcessing(false);
          console.log('✅ Wix checkout URL received with direct forward:', checkoutUrl);
        } else {
          // Process Wix payment via backend (original method)
          const wixPaymentData = {
            cartItems: [{
              title: cartItems[0].title || 'Product',
              quantity: cartItems[0].quantity || 1,
              price: cartItems[0].price || finalTotalInCents
            }],
            totalAmount: finalTotalInCents,
            email: email
          };

          console.log('🔍 Sending Wix Payment Data:', JSON.stringify(wixPaymentData, null, 2));

          const wixPayment = await createWixPayment(wixPaymentData);
          checkoutUrl = wixPayment.checkoutUrl;
          console.log('✅ Wix checkout URL received:', checkoutUrl);
        }
      } else if (paymentMethod === 'stripe_connect') {
        // Check if Stripe Connect is enabled and seller account is specified
        if (!sellerStripeAccountId) {
          alert('Please select a seller or connect a Stripe account first');
          setLoading(false);
          return;
        }

        // Create a connected checkout session
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
        const response = await fetch(`${apiUrl}/create-connected-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: finalTotalInCents,
            currency: 'usd', // or dynamically set based on user's currency
            sellerStripeAccountId: sellerStripeAccountId,
            platformFeePercent: 5 // Configurable platform fee
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Redirect to Stripe Checkout or open Stripe Elements modal
        // This is a simplified example - you'd typically use Stripe.js here
        window.location.href = data.checkoutUrl;
      } else {
        // Process Shopify payment (default)
        // Prepare shipping address if provided
        const shippingAddress = (address1 || city) ? {
          firstName: firstName,
          lastName: lastName,
          address1: address1 || '',
          address2: address2 || '',
          city: city || '',
          province: state || '',
          zip: postal || '',
          country: country || 'US',
          phone: phone || ''
        } : null;

        const draftOrderData = {
          totalAmount: finalTotalInCents, // Amount in cents
          orderNumber: orderNumber,
          email: email,
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          cartItems: cartItems, // Original cart items for reference
          shippingAddress: shippingAddress,
          invoiceTitle: `Custom Invoice - Order ${orderNumber}`,
          // Currency information
          currency: userCurrency,
          currencyData: currencyData,
          originalAmountUSD: finalTotalInCents / 100 // Original amount in USD dollars
        };

        const draftOrder = await createDraftOrderWithCustomPrice(draftOrderData);

        if (!draftOrder || !draftOrder.id) {
          throw new Error('Failed to create checkout');
        }

        // Get the checkout URL
        checkoutUrl = getDraftOrderCheckoutUrl(draftOrder);
        console.log('✅ Shopify checkout created successfully');

        // Save checkout data to Supabase for order tracking (Shopify only)
        if (supabase) {
          const checkoutData = {
            order_number: orderNumber,
            email,
            first_name: firstName,
            last_name: lastName,
            phone: phone,
            address1,
            address2,
            city,
            state,
            postal,
            country,
            cart_items: JSON.stringify(cartItems),
            total_amount: finalTotalInCents,
            currency: currency,
            shopify_draft_order_id: draftOrder.id,
            shopify_checkout_url: checkoutUrl,
            status: 'pending',
            created_at: new Date().toISOString()
          };

          const { error: supabaseError } = await supabase
            .from('abandoned_checkouts')
            .insert([checkoutData]);

          if (supabaseError) {
            console.error('Order tracking save failed (non-blocking):', supabaseError);
          }
        } else {
          console.warn('Supabase not configured - skipping order tracking');
        }
      }

      // Redirect to the appropriate checkout URL (only if not using popup)
      if (paymentMethod !== 'gumroad' || process.env.REACT_APP_GUMROAD_ENABLE_CHECKOUT_POPUP !== 'true') {
        console.log('🔄 Redirecting to checkout (smart-hide uses bridge when possible)');
        await redirectToPaymentOrBridge(checkoutUrl);
      } else {
        console.log('✅ Gumroad popup mode - no redirect needed');
      }

    } catch (error) {
      console.error('Checkout process failed:', error);
      setError(`Unable to create checkout. Please try again or contact support if the issue persists.`);
      setIsProcessing(false);
      setProcessingMessage('');
    } finally {
      setLoading(false);
    }
  };

  // Handle currency change
  const handleCurrencyChange = (newCurrency) => {
    setUserCurrency(newCurrency);
    setCurrency(newCurrency);
    console.log('💱 Currency changed to:', newCurrency);
  };

  const handleLemonCheckout = async (event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    try {
      // Use the dynamically selected payment link from smart discount if available
      let baseUrl = smartDiscount && smartDiscount.paymentLink
        ? smartDiscount.paymentLink
        : lemonCheckoutUrl;

      // Build URL with query parameters
      const url = new URL(baseUrl);

      // Add email parameter if email exists
      if (email && email.includes('@')) {
        url.searchParams.set('checkout[email]', email);
      }

      // Ensure embed=1 and logo=0 are set
      url.searchParams.set('embed', '1');
      url.searchParams.set('logo', '0');

      const checkoutUrl = url.toString();

      // Open Lemon checkout in iframe modal (same page, no redirect)
      setModalUrl(checkoutUrl);
      setModalLoading(true);
      setShowModal(true);
      console.log('✅ Lemon Squeezy checkout opened in iframe modal:', checkoutUrl);
    } catch (error) {
      console.error('Failed to open Lemon Squeezy checkout:', error);
      setError('Failed to open checkout. Please try again.');
    }
  };

  // Modal popup state
  const [showModal, setShowModal] = useState(false);
  const [modalUrl, setModalUrl] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  // Close modal function
  const closeModal = () => {
    setShowModal(false);
    setModalUrl('');
    setModalLoading(false);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && showModal) {
        closeModal();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);



  // Handle direct payment (when shipping form is hidden)
  const handleDirectPayment = async () => {
    setLoading(true);
    setError('');

    try {
      // Get payment method from environment variable
      const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'wix';
      console.log(`🔍 Direct payment using method: ${paymentMethod}`);

      // Calculate the correct total based on whether shipping is included
      const finalTotal = hideShippingForm ? total : totalWithShipping;

      // Prepare order data with minimal required information
      const orderData = {
        cartItems: cartItems,
        totalAmount: finalTotal,
        email: email || 'customer@example.com', // Use provided email or default
        firstName: firstName || 'Customer',
        lastName: lastName || 'Customer',
        phone: phone || '',
        includeShipping: !hideShippingForm, // Flag to indicate if shipping should be included
        shippingAddress: {
          street: address1 || '',
          city: city || '',
          state: state || '',
          zipCode: postal || '',
          country: country || 'US'
        }
      };

      console.log(`Creating direct ${paymentMethod} payment with data:`, orderData);

      let paymentResult;

      // Handle different payment methods
      if (paymentMethod === 'wix') {
        const response = await createWixPayment(orderData);
        paymentResult = response;
      } else if (paymentMethod === 'stripe') {
        // Create Stripe checkout session with proper USD format
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';

        // Prepare Stripe order data with USD dollars (not cents)
        const stripeOrderData = {
          cartItems: cartItems.map(item => ({
            title: item.title || 'Product',
            name: item.title || 'Product',
            description: item.description || 'Product from checkout',
            quantity: item.quantity || 1,
            price: (item.price || 0) / 100, // Convert cents to dollars
            image: item.image || null
          })),
          totalAmount: finalTotal / 100, // Convert cents to dollars
          localTotalAmount: currencyData ? currencyData.paymentAmount : (finalTotal / 100),
          currency: currencyData ? currencyData.paymentCurrency : 'USD',
          country: country || 'US',
          shippingFee: hideShippingForm ? 0 : (shippingFee / 100), // Convert cents to dollars
          email: email || 'customer@example.com',
          firstName: firstName || 'Customer',
          lastName: lastName || 'Customer',
          phone: phone || '',
          orderNumber: orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US'
          } : null
        };

        console.log('🔍 Creating Stripe checkout with USD data:', JSON.stringify(stripeOrderData, null, 2));

        const response = await fetch(`${apiUrl}/create-stripe-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderData: stripeOrderData }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
      } else if (paymentMethod === 'rotation' && !rotationUsesSmart) {
        const rotationOrderData = {
          cartItems: cartItems.map(item => ({
            title: item.title || 'Product',
            name: item.title || 'Product',
            description: item.description || 'Product from checkout',
            quantity: item.quantity || 1,
            price: (item.price || 0) / 100,
            image: item.image || null
          })),
          totalAmount: finalTotal / 100,
          localTotalAmount: currencyData ? currencyData.paymentAmount : (finalTotal / 100),
          currency: currencyData ? currencyData.paymentCurrency : 'USD',
          country: country || 'US',
          shippingFee: hideShippingForm ? 0 : (shippingFee / 100),
          email: email || 'customer@example.com',
          firstName: firstName || 'Customer',
          lastName: lastName || 'Customer',
          phone: phone || '',
          orderNumber: orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US'
          } : null
        };
        const data = await createStripeRotationCheckout(rotationOrderData);
        paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
      } else if (paymentMethod === 'lemonsqueezy') {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
        const lemonsqueezyOrderData = {
          cartItems: cartItems.map(item => ({
            title: item.title || 'Product',
            name: item.title || 'Product',
            description: item.description || 'Product from checkout',
            quantity: item.quantity || 1,
            price: (item.price || 0) / 100, // Convert cents to dollars
            image: item.image || null
          })),
          totalAmount: finalTotal / 100, // Convert cents to dollars
          shippingFee: hideShippingForm ? 0 : (shippingFee / 100), // Convert cents to dollars
          email: email || 'customer@example.com',
          firstName: firstName || 'Customer',
          lastName: lastName || 'Customer',
          phone: phone || '',
          orderNumber: orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US'
          } : null
        };

        console.log('🔍 Creating direct LemonSqueezy checkout with USD data:', JSON.stringify(lemonsqueezyOrderData, null, 2));

        const response = await fetch(`${apiUrl}/create-lemonsqueezy-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderData: lemonsqueezyOrderData }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
      } else if (paymentMethod === 'shopify') {
        // Create Shopify draft order
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
        const response = await fetch(`${apiUrl}/create-draft-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderData }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
      } else if (paymentMethod === 'gumroad') {
        // Handle Gumroad (popup or redirect)
        const gumroadBaseUrl = process.env.REACT_APP_GUMROAD_BASE_URL || 'https://drivishop.gumroad.com/l/mylamb';
        const enableCheckoutPopup = process.env.REACT_APP_GUMROAD_ENABLE_CHECKOUT_POPUP === 'true';

        if (enableCheckoutPopup) {
          // Use modal for Gumroad
          const modalUrl = `${gumroadBaseUrl}?wanted=true&price=${(finalTotal / 100).toFixed(2)}&email=${encodeURIComponent(email || 'customer@example.com')}`;
          setModalUrl(modalUrl);
          setModalLoading(true);
          setShowModal(true);
          setLoading(false);
          return; // Don't redirect, just show modal
        } else {
          // Use redirect for Gumroad
          const checkoutUrl = `${gumroadBaseUrl}?wanted=true&price=${(finalTotal / 100).toFixed(2)}&email=${encodeURIComponent(email || 'customer@example.com')}`;
          paymentResult = { success: true, checkoutUrl };
        }
      } else if (paymentMethod === 'paypal') {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://pay.drivi.pro';
        const paypalOrderData = {
          cartItems,
          totalAmount: Number((finalTotal / 100).toFixed(2)), // dollars
          email: email || 'customer@example.com',
          firstName: firstName || 'Customer',
          lastName: lastName || 'Customer',
          phone: phone || '',
          orderNumber,
          shippingAddress: !hideShippingForm ? {
            street: address1 || '',
            city: city || '',
            state: state || '',
            zipCode: postal || '',
            country: country || 'US',
          } : null,
        };
        const response = await fetch(`${apiUrl}/create-paypal-invoice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderData: paypalOrderData }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || `PayPal invoice failed (${response.status})`);
        }
        const data = await response.json();
        paymentResult = { success: data.success, checkoutUrl: data.checkoutUrl };
      } else if (isSmartFamily || rotationUsesSmart) {
        // Process Smart payment (also used by rotation when cart USD > max)
        if (!smartDiscount || !smartDiscount.paymentLink) {
          throw new Error('Discount expired or no payment link available. Please refresh the page.');
        }

        // Use the payment link from smart discount
        paymentResult = {
          success: true,
          checkoutUrl: smartDiscount.paymentLink
        };
        console.log(
          rotationUsesSmart
            ? '↪️ Rotation >$100 → smart payment link:'
            : '✅ Smart payment checkout URL:',
          smartDiscount.paymentLink
        );
        console.log('💰 Discount applied:', {
          originalTotal: smartDiscount.originalTotal,
          discountedTotal: smartDiscount.discountedTotal,
          discountPercentage: smartDiscount.discountPercentage
        });

        // Save checkout data to Supabase for order tracking (Smart payment - Direct Payment)
        if (supabase) {
          try {
            // Use placeholder email for smart payment since email is required but not collected
            const placeholderEmail = `order-${orderNumber}@smart-checkout.local`;
            // Use actual email for smart-pro, smart-hide, and smart-pro-lemon when shipping is not hidden
            const trackedEmail = (paymentMethod === 'smart-pro' || (paymentMethod === 'smart-hide' && !hideShippingForm) || (paymentMethod === 'smart-pro-lemon' && !hideShippingForm)) && email ? email : placeholderEmail;

            // Use only the columns that exist in the schema: email, cart_data, shipping_info, total, status
            const checkoutData = {
              email: trackedEmail, // Required by schema
              cart_data: {
                items: cartItems, // Cart items as JSONB
                order_number: orderNumber,
                original_total: smartDiscount.originalTotal,
                discounted_total: smartDiscount.discountedTotal,
                discount_percentage: smartDiscount.discountPercentage,
                currency: smartDiscount.targetCurrency || smartDiscount.userCurrency || 'USD',
                payment_method: paymentMethod,
                checkout_url: smartDiscount.paymentLink
              },
              shipping_info: (paymentMethod === 'smart-pro' || (paymentMethod === 'smart-hide' && !hideShippingForm) || (paymentMethod === 'smart-pro-lemon' && !hideShippingForm)) ? {
                firstName: firstName || null,
                lastName: lastName || null,
                address1: address1 || null,
                address2: address2 || null,
                city: city || null,
                state: state || null,
                postal: postal || null,
                country: country || null,
                phone: phone || null
              } : {},
              total: smartDiscount.originalTotal, // Total amount (required by schema)
              status: 'pending' // Status
            };

            const { error: supabaseError } = await supabase
              .from('abandoned_checkouts')
              .insert([checkoutData]);

            if (supabaseError) {
              console.error('Smart payment order tracking save failed (non-blocking):', supabaseError);
              console.error('Error details:', JSON.stringify(supabaseError, null, 2));
            } else {
              console.log('✅ Smart payment order saved to Supabase (direct payment):', orderNumber);
              console.log('📦 Cart items saved:', cartItems.length, 'items');
            }
          } catch (error) {
            console.error('Error saving smart payment order to Supabase (non-blocking):', error);
            console.error('Error stack:', error.stack);
          }
        } else {
          console.warn('Supabase not configured - skipping smart payment order tracking');
        }

        // Save pending order to Neon Database via Backend API (non-blocking)
        try {
          const backendApi = (process.env.REACT_APP_API_URL || 'https://pay.drivi.pro').replace(/\/$/, '');
          const orderDataForBackend = {
            orderNumber: orderNumber,
            email: email || `order-${orderNumber}@smart-checkout.local`,
            firstName: firstName || '',
            lastName: lastName || '',
            phone: phone || '',
            currency: smartDiscount.targetCurrency || smartDiscount.userCurrency || 'USD',
            totalAmount: smartDiscount.originalTotal,
            country: country || 'US',
            cartItems: cartItems
          };
          await fetch(`${backendApi}/create-smart-pending-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderData: orderDataForBackend })
          }).catch(e => console.error('Failed to notify backend of smart order:', e));
        } catch (backendErr) {
          console.error('Error initiating backend save:', backendErr);
        }
      }

      if (paymentResult && paymentResult.success && paymentResult.checkoutUrl) {
        console.log(`✅ ${paymentMethod} payment created successfully:`, paymentResult.checkoutUrl);

        // Track the checkout event
        await trackCheckoutEvent(`${paymentMethod}_payment_created`, {
          total: finalTotal,
          items: cartItems.length,
          method: `${paymentMethod}_direct`,
          includeShipping: !hideShippingForm
        });

        // Redirect to checkout (smart-hide → opaque bridge on API)
        await redirectToPaymentOrBridge(paymentResult.checkoutUrl);
      } else {
        throw new Error(paymentResult?.error || `Failed to create ${paymentMethod} payment link`);
      }
    } catch (error) {
      console.error(`❌ Error creating ${process.env.REACT_APP_PAYMENT_METHOD || 'wix'} payment:`, error);
      setError(`Payment Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = () => {
    // Implement coupon logic here
    console.log('Applying coupon:', coupon);
    // Update total based on coupon
  };

  // Function to clear cart data (for testing purposes)
  const clearCartData = () => {
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(STORE_NAME_KEY);
    setCartItems([]);
    setTotal(0);
    setStore('');
  };

  // Function to update item quantity
  const updateItemQuantity = (index, change) => {
    const updatedCart = [...cartItems];
    const newQuantity = Math.max(1, updatedCart[index].quantity + change);

    // Calculate price difference based on the item's price
    const priceDifference = updatedCart[index].price * (newQuantity - updatedCart[index].quantity);

    // Update item quantity
    updatedCart[index] = {
      ...updatedCart[index],
      quantity: newQuantity
    };

    setCartItems(updatedCart);
    // Update total with the price difference
    setTotal(prevTotal => prevTotal + priceDifference);
  };

  // Function to remove item from cart
  const removeItem = (index) => {
    const itemToRemove = cartItems[index];
    const updatedCart = cartItems.filter((_, i) => i !== index);

    // Subtract the removed item's total price from the cart total
    const newTotal = total - (itemToRemove.price * itemToRemove.quantity);

    setCartItems(updatedCart);
    setTotal(newTotal);

    // If cart becomes empty, also clear customer info and redirect to shop
    if (updatedCart.length === 0) {
      localStorage.removeItem(CART_STORAGE_KEY);
      // Allow a moment for the UI to update before redirecting
      setTimeout(() => {
        const domain = storeDomain || 'drivi.cc';
        window.location.href = `https://${domain}`;
      }, 500);
    }
  };

  // Continue shopping handler
  const handleContinueShopping = () => {
    const domain = storeDomain || 'drivi.cc';
    window.location.href = `https://${domain}`;
  };

  // Function to manually save all form data to localStorage
  const saveFormData = () => {
    try {
      // Save customer info
      const customerInfo = {
        email,
        country,
        firstName,
        lastName,
        address1,
        address2,
        postal,
        phone,
        city,
        state
      };
      localStorage.setItem(CUSTOMER_INFO_KEY, JSON.stringify(customerInfo));

      // Save email separately
      if (email) {
        localStorage.setItem('checkout_email', email);
      }

      // Save cart data
      if (cartItems.length > 0) {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
          items: cartItems,
          total,
          shippingFee
        }));
      }

      console.log('Form data manually saved');
      return true;
    } catch (error) {
      console.error('Error manually saving form data:', error);
      setDebugMessage('Error saving form: ' + error.message);
      return false;
    }
  };

  // Add event listeners to save data when leaving the page
  useEffect(() => {
    // Save data when user is about to leave the page
    const handleBeforeUnload = () => {
      console.log('User is leaving the page, saving form data...');
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [email, firstName, lastName, address1, address2, postal, city, state, phone, cartItems, total]);

  // Do an immediate load on mount to ensure data is available right away
  useEffect(() => {
    // First try to load from localStorage
    const savedCustomerInfo = localStorage.getItem(CUSTOMER_INFO_KEY);
    const savedEmail = localStorage.getItem('checkout_email');

    // Set email directly from checkout_email which is more reliable
    if (savedEmail && savedEmail.includes('@')) {
      setEmail(savedEmail);
      console.log('Immediate load: setting email from checkout_email:', savedEmail);
    }

    if (savedCustomerInfo) {
      try {
        const customerInfo = JSON.parse(savedCustomerInfo);

        // Only set these fields if empty (avoid overwriting)
        if (!firstName) setFirstName(customerInfo.firstName || '');
        if (!lastName) setLastName(customerInfo.lastName || '');
        if (!address1) setAddress1(customerInfo.address1 || '');
        if (!address2) setAddress2(customerInfo.address2 || '');
        if (!postal) setPostal(customerInfo.postal || '');
        if (!phone) setPhone(customerInfo.phone || '');
        if (!city) setCity(customerInfo.city || '');
        if (!state) setState(customerInfo.state || '');

        // Only set email from customerInfo if we don't have it from checkout_email
        if (!email && !savedEmail && customerInfo.email) {
          setEmail(customerInfo.email);
          console.log('Immediate load: setting email from customerInfo:', customerInfo.email);
        }

        console.log('Immediately loaded customer data from localStorage');
      } catch (error) {
        console.error('Error loading customer info from localStorage:', error);
      }
    } else {
      console.log('No customer info found in localStorage for immediate load');
    }
  }, []);

  // Restore button click handler
  const handleRestoreClick = () => {
    // Don't restore if we have a new cart
    if (localStorage.getItem(NEW_CART_FLAG_KEY) === 'true') {
      setDebugMessage('Cannot restore old data with new cart');
      setTimeout(() => setDebugMessage(''), 3000);
      return;
    }

    // Try to load saved customer info
    const savedCustomerInfo = localStorage.getItem(CUSTOMER_INFO_KEY);
    if (savedCustomerInfo) {
      try {
        const customerInfo = JSON.parse(savedCustomerInfo);
        setEmail(customerInfo.email || '');
        setFirstName(customerInfo.firstName || '');
        setLastName(customerInfo.lastName || '');
        setAddress1(customerInfo.address1 || '');
        setAddress2(customerInfo.address2 || '');
        setPostal(customerInfo.postal || '');
        setPhone(customerInfo.phone || '');
        setCity(customerInfo.city || '');
        setState(customerInfo.state || '');
        setCountry(customerInfo.country || 'US');

        setDebugMessage('Your saved information has been restored!');
        setTimeout(() => setDebugMessage(''), 3000);
      } catch (error) {
        console.error('Error loading saved customer info:', error);
        setDebugMessage('Error loading your saved information');
      }
    } else {
      setDebugMessage('No saved information found');
      setTimeout(() => setDebugMessage(''), 3000);
    }
  };



  // Handle postal code validation
  const handlePostalChange = (value) => {
    setPostal(value);

    // Validate postal code
    if (value.length > 0) {
      const isValid = validatePostalCode(value, country);
      setFieldValidation(prev => ({ ...prev, postal: isValid }));
    } else {
      setFieldValidation(prev => ({ ...prev, postal: null }));
    }
  };

  // Handle phone validation
  const handlePhoneChange = (value) => {
    setPhone(value);
    // Phone validation is handled by the PhoneInput component
  };

  // Add a new state for Stripe Connect seller account
  const [sellerStripeAccountId, setSellerStripeAccountId] = useState(null);

  return (
    <div className="app">
      <header className="top-banner" role="banner">
        <div className="top-banner-inner">
          <span className="top-banner-dot" aria-hidden="true" />
          <span className="top-banner-message">{t.topBannerTagline}</span>
        </div>
      </header>

      {/* Add checkout recovery notification */}
      {checkoutRecovered && (
        <div className="checkout-notification">
          <div className="notification-content">
            <BsCheckCircleFill size={20} />
            <span>We've restored your previous checkout information! You can continue where you left off.</span>
            <button className="close-notification" onClick={() => setCheckoutRecovered(false)}>×</button>
          </div>
        </div>
      )}

      {/* Show localStorage debug message if present */}
      {debugMessage && (
        <div className="checkout-notification" style={{ backgroundColor: '#fff3cd', borderColor: '#ffeeba' }}>
          <div className="notification-content" style={{ color: '#856404' }}>
            <span>{debugMessage}</span>
            <button className="close-notification" onClick={() => setDebugMessage('')}>×</button>
          </div>
        </div>
      )}

      <nav className="main-nav" aria-label="Checkout">
        <a href={`https://${storeDomain}`} className="logo">{store}</a>
        <div className="header-actions">

          {/* Language Selector */}
          <select
            value={language}
            onChange={e => {
              const newLanguage = e.target.value;
              setLanguage(newLanguage);
              saveLanguagePreference(newLanguage);
              console.log('Language manually changed to:', newLanguage);
            }}
            className="header-selector"
            aria-label="Language"
          >
            <option value="en">EN</option>
            <option value="fr">FR</option>
            <option value="es">ES</option>
            <option value="de">DE</option>
          </select>

          {/* Currency Selector */}
          {exchangeRatesLoaded && (
            <select
              value={userCurrency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              className="header-selector header-selector-currency"
              aria-label="Currency"
            >
              {/* Show all available currencies for conversion, not just payment-supported ones */}
              {Object.keys(CURRENCY_SYMBOLS).map(currency => (
                <option key={currency} value={currency}>
                  {CURRENCY_FLAGS[currency] || '💰'} {CURRENCY_SYMBOLS[currency]} {currency}
                </option>
              ))}
            </select>
          )}
          {!exchangeRatesLoaded && (
            <div className="header-currency-loading">{t.loadingRates}</div>
          )}

          <button onClick={handleContinueShopping} className="continue-shopping-btn">
            {t.continueShopping}
          </button>
        </div>
      </nav>

      {/* Smart Payment Discount Banner - Mobile Top Position */}
      {!hideAnnouncement && usesSmartLinks && smartDiscount && (
        <div className="mobile-discount-banner-top">
          <div className="discount-banner-chic" role="region" aria-label={t.specialDiscountOffer}>
            <div className="discount-banner-chic-inner">
              <div className="discount-banner-chic-main">
                <div className="discount-banner-chic-topline">
                  <span className="discount-banner-chic-eyebrow">{t.specialDiscountOffer}</span>
                  <div className="discount-badges">
                    <span className="discount-pill discount-pill-accent">
                      {smartDiscount.discountPercentage.toFixed(1)}% {t.off}
                    </span>
                    {(paymentMethod === 'smart' || paymentMethod === 'smart-hide' || paymentMethod === 'smart-pro-lemon') &&
                      hideShippingForm &&
                      !hideAnnouncementFreeShipping && (
                        <span className="discount-pill discount-pill-free">{t.freeShipping}</span>
                      )}
                  </div>
                </div>
                <div className="discount-banner-chic-price-row">
                  <div className="discount-price-lines">
                    <div className="discount-price-line">
                      <span className="discount-price-label">{t.original}</span>
                      <span className="discount-price-old">
                        {(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? (
                          formatCurrency(smartDiscount.originalTotalLocal, smartDiscount.userCurrency)
                        ) : smartDiscount.userCurrency !== smartDiscount.targetCurrency ? (
                          <>
                            {formatCurrency(smartDiscount.originalTotalLocal, smartDiscount.userCurrency)}{' '}
                            <span className="discount-price-fx">({formatCurrency(smartDiscount.originalTotal, smartDiscount.targetCurrency || 'USD')})</span>
                          </>
                        ) : (
                          formatCurrency(smartDiscount.originalTotal, smartDiscount.targetCurrency || 'USD')
                        )}
                      </span>
                    </div>
                    <div className="discount-price-line discount-price-line-now">
                      <span className="discount-price-label discount-price-label-now">{t.now}</span>
                      <span className="discount-price-now">
                        {(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? (
                          formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency)
                        ) : smartDiscount.userCurrency !== smartDiscount.targetCurrency ? (
                          <>
                            {formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency)}{' '}
                            <span className="discount-price-fx">({formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')})</span>
                          </>
                        ) : (
                          formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="discount-save-inline">
                    <span className="discount-save-chip">
                      {t.save}{' '}
                      {(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? (
                        formatCurrency(smartDiscount.discountAmountLocal, smartDiscount.userCurrency)
                      ) : smartDiscount.userCurrency !== smartDiscount.targetCurrency ? (
                        <>
                          {formatCurrency(smartDiscount.discountAmountLocal, smartDiscount.userCurrency)}{' '}
                          <span className="discount-price-fx">({formatCurrency(smartDiscount.discountAmount, smartDiscount.targetCurrency || 'USD')})</span>
                        </>
                      ) : (
                        formatCurrency(smartDiscount.discountAmount, smartDiscount.targetCurrency || 'USD')
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="discount-timer-wrap discount-expired-today-wrap">
                <div className="discount-expired-today" role="status">
                  {t.expiredToday}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {cartItems.length === 0 ? (
        <div className="empty-cart-message">
          <h2>{t.emptyCart}</h2>
          <p>{t.emptyCartDesc}</p>
          <button
            onClick={() => {
              const domain = storeDomain || 'drivi.pro';
              window.location.href = `https://${domain}`;
            }}
            className="return-button"
          >
            {t.returnToShop}
          </button>
        </div>
      ) : (
        <div className="checkout-container">
          {/* Processing overlay for auto-redirect */}
          {isProcessing && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              color: 'white',
              fontSize: '18px',
              flexDirection: 'column',
              gap: '20px'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #ffffff40',
                borderTop: '4px solid #ffffff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>Please wait while we forward you to checkout...</div>
                <div>{processingMessage}</div>
              </div>
            </div>
          )}

          {/* Checkout Modal Popup (Gumroad or Lemon Squeezy) */}
          {showModal && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: 0,
                backdropFilter: 'blur(4px)'
              }}
              onClick={closeModal} // Close when clicking outside
            >
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: 0,
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 32px 96px rgba(0,0,0,0.25)',
                  animation: 'modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
              >
                {/* Close Button - Positioned absolutely in top right */}
                <button
                  onClick={closeModal}
                  style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid #e5e7eb',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    zIndex: 10001,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                    e.target.style.transform = 'scale(1)';
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                  }}
                >
                  ×
                </button>

                {/* Modal Content - Full width and height iframe */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {modalLoading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                      backdropFilter: 'blur(2px)'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '20px'
                      }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          border: '3px solid #f3f4f6',
                          borderTop: '3px solid #6b7280',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <div style={{
                          color: '#374151',
                          fontSize: '15px',
                          fontWeight: '500'
                        }}>
                          Loading secure checkout...
                        </div>
                      </div>
                    </div>
                  )}


                  <iframe
                    src={modalUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: 0
                    }}
                    title="Checkout"
                    allow="payment"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    onLoad={() => setModalLoading(false)}
                  />
                </div>
              </div>
            </div>
          )}
          {hideShippingForm ? (
            // Simplified checkout - only cart and checkout button
            <div className="simplified-checkout">
              <div className="order-summary-only">
                <h2>{t.orderSummary}</h2>

                {/* Error and Success Messages */}
                {error && (
                  <div style={{
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '12px',
                    marginBottom: '20px',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div style={{
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '12px',
                    marginBottom: '20px',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    {successMessage}
                  </div>
                )}

                {/* Currency Info */}
                <CurrencyInfo
                  currencyData={currencyData}
                  paymentMethod={process.env.REACT_APP_PAYMENT_METHOD || 'wix'}
                  t={t}
                  className="mb-4"
                />

                <div className="cart-items">
                  {cartItems.map((item, index) => (
                    <div key={index} className="product-item">
                      <div className="product-item-image-wrap">
                        <img src={item.image} alt={item.title} />
                      </div>
                      <div className="product-details">
                        <h3>{item.title}</h3>
                        <div className="quantity-controls">
                          <button
                            type="button"
                            className="quantity-btn"
                            onClick={() => updateItemQuantity(index, -1)}
                            disabled={item.quantity <= 1}
                          >
                            -
                          </button>
                          <span className="quantity-display">{item.quantity}</span>
                          <button
                            type="button"
                            className="quantity-btn"
                            onClick={() => updateItemQuantity(index, 1)}
                          >
                            +
                          </button>
                        </div>
                        <p>{t.price}: <PriceDisplay priceInCents={item.price} currencyData={currencyData} /></p>
                        <button
                          type="button"
                          className="remove-item-btn"
                          onClick={() => removeItem(index)}
                        >
                          {t.remove}
                        </button>
                      </div>
                      <div className="product-price">
                        <PriceDisplay priceInCents={item.price * item.quantity} currencyData={currencyData} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Currency Processing Notification - Under Cart Details */}
                {usesSmartLinks && smartDiscount && smartDiscount.userCurrency !== (smartDiscount.targetCurrency || 'USD') && (
                  <div style={{
                    background: 'transparent',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '24px',
                    color: '#000000',
                    fontSize: '14px',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    {smartDiscount.targetCurrency === 'EUR'
                      ? t.processedInEUR
                      : t.processedInUSD
                    }
                  </div>
                )}

                {/* Hide coupon section for smart payment */}
                {paymentMethod !== 'smart' && !(paymentMethod === 'smart-hide' && hideShippingForm) && paymentMethod !== 'smart-pro-lemon' && !rotationUsesSmart && (
                  <div className="coupon-section">
                    <h3>{t.discount}</h3>
                    <div className="coupon-input">
                      <input type="text" placeholder={t.couponPlaceholder} value={coupon} onChange={(e) => setCoupon(e.target.value)} />
                      <button type="button" onClick={handleApplyCoupon}>{t.apply}</button>
                    </div>
                  </div>
                )}

                <div className="order-totals">
                  {usesSmartLinks && smartDiscount ? (
                    <>
                      <div className="subtotal">
                        <span>{t.subtotal}</span>
                        <span>
                          {(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? (
                            formatCurrency(smartDiscount.originalTotalLocal, smartDiscount.userCurrency)
                          ) : smartDiscount.userCurrency !== smartDiscount.targetCurrency ? (
                            <>
                              {formatCurrency(smartDiscount.originalTotalLocal, smartDiscount.userCurrency)}{' '}
                              <span className="text-xs text-gray-500">
                                ({formatCurrency(smartDiscount.originalTotal, smartDiscount.targetCurrency || 'USD')})
                              </span>
                            </>
                          ) : (
                            formatCurrency(smartDiscount.originalTotal, smartDiscount.targetCurrency || 'USD')
                          )}
                        </span>
                      </div>
                      {smartDiscount.discountAmountLocal > 0 && (
                        <div className="order-savings">
                          <span>{t.save}</span>
                          <span>
                            -
                            {formatCurrency(
                              smartDiscount.discountAmountLocal,
                              smartDiscount.userCurrency || currencyData?.localCurrency || 'USD'
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="subtotal">
                      <span>{t.subtotal}</span>
                      <span><PriceDisplay priceInCents={total} currencyData={currencyData} /></span>
                    </div>
                  )}
                  {!hideShippingForm && (
                    <div className="shipping">
                      <span>{t.shipping}</span>
                      <span><PriceDisplay priceInCents={shippingFee} currencyData={currencyData} /></span>
                    </div>
                  )}
                </div>

                <div className="total">
                  {usesSmartLinks && smartDiscount ? (
                    <div className="currency-display single-currency">
                      <span className="currency-label text-gray-600 mr-2">{t.total}:</span>
                      <span className="currency-amount text-lg font-bold text-gray-900">
                        {(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? (
                          formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency)
                        ) : smartDiscount.userCurrency !== smartDiscount.targetCurrency ? (
                          <>
                            {formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency)}{' '}
                            <span className="text-sm font-normal text-gray-500">
                              ({formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')})
                            </span>
                          </>
                        ) : (
                          formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')
                        )}
                      </span>
                    </div>
                  ) : (
                    <CurrencyDisplay
                      currencyData={currencyData}
                      labelText={t.total}
                      size="large"
                      t={t}
                    />
                  )}
                </div>

                <TrustBadges />

                {/* Email input for hidden shipping form - Hide for smart payment and smart-pro-lemon when shipping is hidden */}
                {paymentMethod !== 'smart' && !(paymentMethod === 'smart-hide' && hideShippingForm) && !(paymentMethod === 'smart-pro-lemon' && hideShippingForm) && !rotationUsesSmart && (
                  <div className="minimal-contact-info">
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="20" width="20" style={{ color: '#6b7280' }}>
                        <path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"></path>
                      </svg>
                      Contact Information
                    </h3>
                    <div style={{
                      display: 'flex',
                      position: 'relative',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        position: 'relative',
                        width: '100%'
                      }}>
                        <input
                          type="email"
                          placeholder={t.emailPlaceholder}
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (!localStorage.getItem(NEW_CART_FLAG_KEY) && e.target.value && e.target.value.includes('@')) {
                              localStorage.setItem('checkout_email', e.target.value);
                            }
                          }}
                          required
                          style={{
                            width: '100%',
                            padding: '16px 20px',
                            paddingLeft: '48px',
                            border: email && !email.includes('@') ? '2px solid #ef4444' : '2px solid #e5e7eb',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: '500',
                            backgroundColor: '#ffffff',
                            color: '#1f2937',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            outline: 'none'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#3b82f6';
                            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = email && !email.includes('@') ? '#ef4444' : '#e5e7eb';
                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                          }}
                        />
                        <svg
                          stroke="currentColor"
                          fill="currentColor"
                          strokeWidth="0"
                          viewBox="0 0 512 512"
                          height="20"
                          width="20"
                          style={{
                            position: 'absolute',
                            left: '16px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: email && !email.includes('@') ? '#ef4444' : '#9ca3af',
                            transition: 'color 0.2s ease'
                          }}
                        >
                          <path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"></path>
                        </svg>
                        <button
                          type="button"
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '8px',
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Load saved information"
                          onClick={handleRestoreClick}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                            e.target.style.color = '#374151';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#6b7280';
                          }}
                        >
                          <FaHistory size={16} />
                        </button>
                      </div>
                    </div>
                    {email && !email.includes('@') && (
                      <div style={{
                        color: '#ef4444',
                        fontSize: '14px',
                        marginTop: '-12px',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="14" width="14">
                          <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zm32 224c0 17.7-14.3 32-32 32s-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32z"></path>
                        </svg>
                        Please enter a valid email address
                      </div>
                    )}
                  </div>
                )}

                {paymentMethod === 'smart-pro-lemon' ? (
                  <button
                    type="button"
                    className={`checkout-button ${(paymentMethod === 'smart' || paymentMethod === 'smart-hide' || rotationUsesSmart) ? 'checkout-button-smart' : ''}`}
                    onClick={handleLemonCheckout}
                    disabled={loading || !smartDiscount || !smartDiscount.paymentLink}
                    style={{
                      opacity: (loading || !smartDiscount || !smartDiscount.paymentLink) ? 0.6 : 1,
                      cursor: (loading || !smartDiscount || !smartDiscount.paymentLink) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff40',
                          borderTop: '2px solid #ffffff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Creating your secure checkout...
                      </span>
                    ) : (
                      currencyData && smartDiscount ? (
                        hidePromo ? (
                          // Simple text without promo when hidePromo is true
                          `${t.processPayment} - ${(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')}`
                        ) : (
                          // Full promo text with savings
                          (() => {
                            const targetCurr = smartDiscount.targetCurrency || 'USD';
                            const amountText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, targetCurr);
                            const saveText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountAmountLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountAmount, targetCurr);
                            return t.proceedToCheckoutSave.replace('${amount}', amountText).replace('${saveAmount}', saveText);
                          })()
                        )
                      ) : (
                        t.processPayment
                      )
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleDirectPayment}
                    className={`checkout-button ${(paymentMethod === 'smart' || paymentMethod === 'smart-hide' || rotationUsesSmart) ? 'checkout-button-smart' : ''}`}
                    disabled={
                      loading || (
                        (paymentMethod !== 'smart' && !(paymentMethod === 'smart-hide' && hideShippingForm) && paymentMethod !== 'smart-pro' && paymentMethod !== 'smart-pro-lemon' && !rotationUsesSmart && (!email || !email.includes('@'))) ||
                        (usesSmartLinks && (!smartDiscount || !smartDiscount.paymentLink))
                      )
                    }
                    style={{
                      opacity: ((paymentMethod !== 'smart' && !(paymentMethod === 'smart-hide' && hideShippingForm) && paymentMethod !== 'smart-pro' && paymentMethod !== 'smart-pro-lemon' && !rotationUsesSmart && (!email || !email.includes('@'))) || (usesSmartLinks && (!smartDiscount || !smartDiscount.paymentLink))) ? 0.6 : 1,
                      cursor: ((paymentMethod !== 'smart' && !(paymentMethod === 'smart-hide' && hideShippingForm) && paymentMethod !== 'smart-pro' && paymentMethod !== 'smart-pro-lemon' && !rotationUsesSmart && (!email || !email.includes('@'))) || (usesSmartLinks && (!smartDiscount || !smartDiscount.paymentLink))) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff40',
                          borderTop: '2px solid #ffffff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Creating your secure checkout...
                      </span>
                    ) : (
                      currencyData ? (
                        usesSmartLinks && smartDiscount ? (
                          hidePromo ? (
                            // Simple text without promo when hidePromo is true
                            `${t.processPayment} - ${(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')}`
                          ) : (
                            // Full promo text with savings
                            (() => {
                              const targetCurr = smartDiscount.targetCurrency || 'USD';
                              const amountText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, targetCurr);
                              const saveText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountAmountLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountAmount, targetCurr);
                              return t.proceedToCheckoutSave.replace('${amount}', amountText).replace('${saveAmount}', saveText);
                            })()
                          )
                        ) : process.env.REACT_APP_PAYMENT_METHOD === 'mypos' ? (
                          // myPOS: Show appropriate currency handling
                          (() => {
                            const currency = currencyData.localCurrency;

                            // For USD, show direct payment (no conversion)
                            if (currency === 'USD') {
                              return `Proceed to Checkout - ${formatCurrency(currencyData.localAmount, 'USD')}`;
                            }

                            // For EUR, show direct payment (no conversion)
                            if (currency === 'EUR') {
                              return `Proceed to Checkout - ${formatCurrency(currencyData.localAmount, 'EUR')}`;
                            }

                            // For other currencies, show conversion to EUR
                            const conversionRates = {
                              'EUR': 1.0, 'USD': 0.92, 'GBP': 1.17, 'CAD': 0.68, 'AUD': 0.61, 'JPY': 0.0061,
                              'CHF': 1.05, 'SEK': 0.085, 'NOK': 0.085, 'DKK': 0.13, 'PLN': 0.22, 'CZK': 0.039,
                              'HUF': 0.0025, 'BRL': 0.18, 'MXN': 0.054, 'SGD': 0.68, 'HKD': 0.12, 'MYR': 0.20,
                              'INR': 0.011, 'THB': 0.025, 'NZD': 0.56, 'ZAR': 0.051, 'ILS': 0.25, 'KRW': 0.00069,
                              'CNY': 0.13, 'RUB': 0.0095, 'TRY': 0.030, 'IDR': 0.000059, 'PHP': 0.016, 'VND': 0.000037,
                              'TWD': 0.029, 'SAR': 0.25, 'AED': 0.25, 'EGP': 0.030, 'NGN': 0.00069, 'GHS': 0.069,
                              'KES': 0.0069, 'UGX': 0.00025, 'TZS': 0.00037, 'MAD': 0.092, 'TND': 0.30, 'DZD': 0.0069
                            };
                            const eurAmount = currencyData.localAmount * (conversionRates[currency] || 0.92);
                            return `Proceed to Checkout - ${formatCurrency(currencyData.localAmount, currencyData.localCurrency)} (${formatCurrency(eurAmount, 'EUR')} EUR)`;
                          })()
                        ) : currencyData.showDualCurrency ?
                          `Proceed to Checkout - ${formatCurrency(currencyData.localAmount, currencyData.localCurrency)} (${formatCurrency(currencyData.usdAmount, 'USD')} USD)` :
                          `Proceed to Checkout - ${formatCurrency(currencyData.localAmount, currencyData.localCurrency)}`
                      ) : (
                        `Proceed to Checkout - $${((hideShippingForm ? total : totalWithShipping) / 100).toFixed(2)}`
                      )
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : (
            // Full checkout form
            <div className="checkout-content">
              <form onSubmit={handleSubmit} className="checkout-form">

                {/* Error and Success Messages */}
                {error && (
                  <div
                    className="checkout-notification"
                    style={{
                      backgroundColor: '#f8d7da',
                      color: '#721c24',
                      padding: '12px',
                      marginBottom: '20px',
                      border: '1px solid #f5c6cb',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div
                    className="checkout-notification"
                    style={{
                      backgroundColor: '#d4edda',
                      color: '#155724',
                      padding: '12px',
                      marginBottom: '20px',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                    {successMessage}
                  </div>
                )}

                <div className="customer-info">
                  <p className="checkout-microcopy">{t.almostDone}</p>

                  <h2>{t.contactInfo}</h2>
                  <div style={{ display: 'flex', position: 'relative' }}>
                    {!hideShippingForm && (
                      <>
                        <input
                          type="email"
                          placeholder={t.emailPlaceholder}
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (!localStorage.getItem(NEW_CART_FLAG_KEY) && e.target.value && e.target.value.includes('@')) {
                              localStorage.setItem('checkout_email', e.target.value);
                            }
                          }}
                          required
                          style={{ width: '100%' }}
                        />
                        <button
                          type="button"
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '40%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#3182ce',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20px',
                            height: '20px',
                            padding: '0'
                          }}
                          title="Load saved information"
                          onClick={handleRestoreClick}
                        >
                          <FaHistory size={16} />
                        </button>
                      </>
                    )}
                  </div>
                  {!hideShippingForm && (
                    <label className="checkbox-label">
                      <input type="checkbox" /> {t.newsOffers}
                    </label>
                  )}

                  <h2>{t.shippingInfo}</h2>
                  <div style={{ marginBottom: '15px', position: 'relative' }}>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={countryOptions}
                      value={countryOptions.find(opt => opt.value === country)}
                      onChange={opt => {
                        setCountry(opt.value);
                        saveCountryPreference(opt.value);
                        console.log('Country manually changed to:', opt.value);
                      }}
                      placeholder={t.shippingInfo}
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 44,
                          borderRadius: 6,
                          borderColor: '#e2e8f0',
                          boxShadow: '0 2px 8px rgba(50, 50, 93, 0.04)',
                          fontSize: 16,
                          background: '#f9fafb',
                        }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: 16,
                          background: state.isSelected ? '#e0e7ff' : state.isFocused ? '#f0f4f8' : '#fff',
                          color: '#2d3748',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          display: 'flex',
                          alignItems: 'center',
                        }),
                      }}
                    />
                    {countryDetected && (
                      <div className="country-detection-info">
                        <FaGlobe size={10} />
                        <span>Auto-detected</span>
                      </div>
                    )}
                  </div>
                  <div className="name-fields">
                    <input
                      type="text"
                      placeholder={t.firstName}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      onBlur={saveFormData}
                      required
                    />
                    <input
                      type="text"
                      placeholder={t.lastName}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      onBlur={saveFormData}
                      required
                    />
                  </div>
                  <AddressAutocomplete
                    value={address1}
                    onChange={setAddress1}
                    onAddressSelect={(parsed) => {
                      if (parsed.city) setCity(parsed.city);
                      if (parsed.state) setState(parsed.state);
                      if (parsed.postal) setPostal(parsed.postal);
                      if (parsed.country && parsed.country !== country) {
                        setCountry(parsed.country);
                        saveCountryPreference(parsed.country);
                      }
                    }}
                    placeholder={t.address1}
                    className="form-control"
                    countryCode={country}
                    required
                  />
                  <input
                    type="text"
                    placeholder={t.address2}
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    onBlur={saveFormData}
                  />
                  {(() => {
                    const visibleFields = countryConfig.addressFormat.filter((f) => ['city', 'postal', 'state'].includes(f));
                    const gridColsClass = visibleFields.length === 3 ? 'md:grid-cols-3' : visibleFields.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';

                    return (
                      <div className={`address-line-dynamic grid grid-cols-1 ${gridColsClass} gap-4 mb-4`}>
                        {visibleFields.map((fieldKey) => {
                          if (fieldKey === 'city') {
                            return (
                              <input
                                key="city"
                                type="text"
                                placeholder={t.city}
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                onBlur={saveFormData}
                                required
                              />
                            );
                          }
                          if (fieldKey === 'postal') {
                            return (
                              <input
                                key="postal"
                                type="text"
                                placeholder={countryConfig.postalLabel || t.postal}
                                value={postal}
                                onChange={(e) => handlePostalChange(e.target.value)}
                                onBlur={saveFormData}
                                className={fieldValidation.postal === false ? 'field-invalid' : fieldValidation.postal === true ? 'field-valid' : ''}
                                style={{
                                  borderColor: fieldValidation.postal === false ? '#e53e3e' : fieldValidation.postal === true ? '#48bb78' : '#e2e8f0'
                                }}
                              />
                            );
                          }
                          // state
                          return (
                            <input
                              key="state"
                              type="text"
                              placeholder={countryConfig.stateLabel || t.state}
                              value={state}
                              onChange={(e) => setState(e.target.value)}
                              onBlur={saveFormData}
                              required={!!countryConfig.stateRequired}
                            />
                          );
                        })}
                      </div>
                    );
                  })()}
                  <PhoneInput
                    value={phone}
                    onChange={handlePhoneChange}
                    countryCode={country}
                    placeholder={t.phone}
                    required
                  />

                  <h2>{t.shippingMethod}</h2>
                  <div className="shipping-method-box shipping-selected">
                    <div className="shipping-recommended-badge">⭐ {t.recommended}</div>
                    <div className="shipping-option">
                      <div className="shipping-option-header">
                        <div className="shipping-icon">
                          <FaShippingFast size={14} />
                        </div>
                        <div className="shipping-option-details">
                          <span className="shipping-option-name">{t.fedex}</span>
                          <span className="shipping-delivery-estimate">{t.deliveryEstimate}</span>
                        </div>
                      </div>
                      <p className="shipping-price">
                        <PriceDisplay priceInCents={shippingFee} currencyData={currencyData} />
                      </p>
                    </div>
                  </div>

                  <h2>{t.paymentMethod}</h2>
                  <div className="payment-method-box">
                    <div className="payment-method-header">
                      <div className="lock-icon">
                        <FaLock size={16} />
                      </div>
                      <span>{t.secureCheckout}</span>
                    </div>
                    <p>{t.secureDesc}</p>
                    <PaymentIcons />
                  </div>

                  {paymentMethod === 'smart-pro-lemon' ? (
                    <button
                      type="button"
                      className={`checkout-button ${(paymentMethod === 'smart' || paymentMethod === 'smart-hide' || rotationUsesSmart) ? 'checkout-button-smart' : ''}`}
                      onClick={handleLemonCheckout}
                      disabled={!formValid || loading || isProcessing || !smartDiscount || !smartDiscount.paymentLink}
                    >
                      {loading || isProcessing ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <span style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #ffffff40',
                            borderTop: '2px solid #ffffff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></span>
                          {isProcessing ? processingMessage : 'Creating your secure checkout...'}
                        </span>
                      ) : (
                        currencyData && smartDiscount ? (
                          hidePromo ? (
                            // Simple text without promo when hidePromo is true
                            `${t.processPayment} - ${(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')}`
                          ) : (
                            // Full promo text with savings
                            (() => {
                              const targetCurr = smartDiscount.targetCurrency || 'USD';
                              const amountText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, targetCurr);
                              const saveText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountAmountLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountAmount, targetCurr);
                              return `${t.processPayment} - ${amountText} (${t.save} ${saveText})`;
                            })()
                          )
                        ) : (
                          t.processPayment
                        )
                      )}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className={`checkout-button ${(paymentMethod === 'smart' || paymentMethod === 'smart-hide' || rotationUsesSmart) ? 'checkout-button-smart' : ''}`}
                      disabled={!formValid || loading || isProcessing}
                    >
                      {loading || isProcessing ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <span style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #ffffff40',
                            borderTop: '2px solid #ffffff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></span>
                          {isProcessing ? processingMessage : 'Creating your secure checkout...'}
                        </span>
                      ) : (
                        currencyData ? (
                          usesSmartLinks && smartDiscount ? (
                            hidePromo ? (
                              // Simple text without promo when hidePromo is true
                              `${t.processPayment} - ${(smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, smartDiscount.targetCurrency || 'USD')}`
                            ) : (
                              // Full promo text with savings
                              (() => {
                                const targetCurr = smartDiscount.targetCurrency || 'USD';
                                const amountText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountedTotalLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountedTotal, targetCurr);
                                const saveText = (smartDiscount.userCurrency === 'EUR' || smartDiscount.userCurrency === 'GBP') ? formatCurrency(smartDiscount.discountAmountLocal, smartDiscount.userCurrency) : formatCurrency(smartDiscount.discountAmount, targetCurr);
                                return `${t.processPayment} - ${amountText} (${t.save} ${saveText})`;
                              })()
                            )
                          ) : process.env.REACT_APP_PAYMENT_METHOD === 'mypos' ? (
                            // myPOS: Show appropriate currency handling
                            (() => {
                              const currency = currencyData.localCurrency;

                              // For USD, show direct payment (no conversion)
                              if (currency === 'USD') {
                                return `${t.processPayment} - ${formatCurrency(currencyData.localAmount, 'USD')}`;
                              }

                              // For EUR, show direct payment (no conversion)
                              if (currency === 'EUR') {
                                return `${t.processPayment} - ${formatCurrency(currencyData.localAmount, 'EUR')}`;
                              }

                              // For other currencies, show conversion to EUR
                              const conversionRates = {
                                'EUR': 1.0, 'USD': 0.92, 'GBP': 1.17, 'CAD': 0.68, 'AUD': 0.61, 'JPY': 0.0061,
                                'CHF': 1.05, 'SEK': 0.085, 'NOK': 0.085, 'DKK': 0.13, 'PLN': 0.22, 'CZK': 0.039,
                                'HUF': 0.0025, 'BRL': 0.18, 'MXN': 0.054, 'SGD': 0.68, 'HKD': 0.12, 'MYR': 0.20,
                                'INR': 0.011, 'THB': 0.025, 'NZD': 0.56, 'ZAR': 0.051, 'ILS': 0.25, 'KRW': 0.00069,
                                'CNY': 0.13, 'RUB': 0.0095, 'TRY': 0.030, 'IDR': 0.000059, 'PHP': 0.016, 'VND': 0.000037,
                                'TWD': 0.029, 'SAR': 0.25, 'AED': 0.25, 'EGP': 0.030, 'NGN': 0.00069, 'GHS': 0.069,
                                'KES': 0.0069, 'UGX': 0.00025, 'TZS': 0.00037, 'MAD': 0.092, 'TND': 0.30, 'DZD': 0.0069
                              };
                              const eurAmount = currencyData.localAmount * (conversionRates[currency] || 0.92);
                              return `${t.processPayment} - ${formatCurrency(currencyData.localAmount, currencyData.localCurrency)} (${formatCurrency(eurAmount, 'EUR')} EUR)`;
                            })()
                          ) : currencyData.showDualCurrency ?
                            `${t.processPayment} - ${formatCurrency(currencyData.localAmount, currencyData.localCurrency)} (${formatCurrency(currencyData.usdAmount, 'USD')} USD)` :
                            `${t.processPayment} - ${formatCurrency(currencyData.localAmount, currencyData.localCurrency)}`
                        ) : (
                          `${t.processPayment} - $${((hideShippingForm ? total : totalWithShipping) / 100).toFixed(2)}`
                        )
                      )}
                    </button>
                  )}

                  <div className="checkout-reassurance">
                    <FaLock className="checkout-reassurance-icon" size={12} />
                    <span>{t.reassurance}</span>
                  </div>

                </div>

                <div>
                  <div className="order-summary-toggle">
                    <button
                      type="button"
                      className="order-summary-toggle-btn"
                      aria-expanded={mobileOrderSummaryOpen}
                      onClick={() => setMobileOrderSummaryOpen(v => !v)}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaShippingFast size={14} />
                        {t.orderSummary} ({cartItems.length})
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CurrencyDisplay currencyData={currencyData} labelText="" size="small" t={t} />
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </span>
                    </button>
                  </div>

                  <div className={`order-summary ${mobileOrderSummaryOpen ? '' : 'order-summary-collapsed'}`}>
                    <h2>{t.orderSummary}</h2>

                    <CurrencyInfo
                      currencyData={currencyData}
                      paymentMethod={process.env.REACT_APP_PAYMENT_METHOD || 'wix'}
                      t={t}
                      className="mb-4"
                    />

                    <div className="cart-items">
                      {cartItems.map((item, index) => (
                        <div key={index} className="product-item">
                          <div className="product-item-image-wrap">
                            <img src={item.image} alt={item.title} />
                          </div>
                          <div className="product-details">
                            <h3>{item.title}</h3>
                            <div className="quantity-controls">
                              <button
                                type="button"
                                className="quantity-btn"
                                onClick={() => updateItemQuantity(index, -1)}
                                disabled={item.quantity <= 1}
                              >
                                -
                              </button>
                              <span className="quantity-display">{item.quantity}</span>
                              <button
                                type="button"
                                className="quantity-btn"
                                onClick={() => updateItemQuantity(index, 1)}
                              >
                                +
                              </button>
                            </div>
                            <p>{t.price}: <PriceDisplay priceInCents={item.price} currencyData={currencyData} /></p>
                            <button
                              type="button"
                              className="remove-item-btn"
                              onClick={() => removeItem(index)}
                            >
                              {t.remove}
                            </button>
                          </div>
                          <div className="product-price">
                            <PriceDisplay priceInCents={item.price * item.quantity} currencyData={currencyData} />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="coupon-section">
                      <h3>{t.discount}</h3>
                      <div className="coupon-input">
                        <input type="text" placeholder={t.couponPlaceholder} value={coupon} onChange={(e) => setCoupon(e.target.value)} />
                        <button type="button" onClick={handleApplyCoupon}>{t.apply}</button>
                      </div>
                    </div>

                    <div className="order-totals">
                      <div className="subtotal">
                        <span>{t.subtotal}</span>
                        <span><PriceDisplay priceInCents={total} currencyData={currencyData} /></span>
                      </div>
                      {!hideShippingForm && (
                        <div className="shipping">
                          <span>{t.shipping}</span>
                          <span><PriceDisplay priceInCents={shippingFee} currencyData={currencyData} /></span>
                        </div>
                      )}
                      {smartDiscount && smartDiscount.discountAmountLocal > 0 && (
                        <div className="order-savings">
                          <span>{t.save}</span>
                          <span>-{formatCurrency(smartDiscount.discountAmountLocal, smartDiscount.userCurrency || currencyData?.localCurrency || 'USD')}</span>
                        </div>
                      )}
                    </div>

                    <div className="total">
                      <CurrencyDisplay
                        currencyData={currencyData}
                        labelText={t.total}
                        size="large"
                        t={t}
                      />
                    </div>

                    {!hideShippingForm && (
                      <div className="order-delivery-estimate">
                        <FaShippingFast size={14} />
                        <span>{t.deliveryEstimate}</span>
                      </div>
                    )}

                    <TrustBadges />
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckoutForm;